import _ from 'lodash'
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'
import { DXFWriter } from '@/utils/dxfUtils'
import EditorDatumUtils from '@glass-designer/utils/editorDatumUtils'
import EditorMaterialUtils from '../utils/editorMaterialUtils'
import EditorFactory from '@glass-designer/utils/editorFactory'
import GlassShape from '@glass-designer/model/glassShape'
import GlassPanelItem from '@glass-designer/model/glassPanelItem'
import GlassDatumLine from '@glass-designer/model/glassDatumLine'
import GlassSplitLine from '@glass-designer/model/glassSplitLine'
import GlassBaseHole from '@glass-designer/model/glassBaseHole'
import AssemblyGroup from '@glass-designer/model/assemblyGroup'
import Order from '@glass-designer/model/order'

import GlassGrid from '@glass-designer/objects/glassGrid'
import BaseDimension from '@glass-designer/objects/baseDimension'
import LinearDimension from '@glass-designer/objects/linearDimension'
import AngularDimension from '@glass-designer/objects/angularDimension'
import EditingMoveGroupHandle from '@/apps/glass-designer/src/objects/editingMoveGroupHandle'
import ReviewHighlightObject from '@/apps/glass-designer/src/objects/reviewHighlightObject'

import DimensionHelper from '@glass-designer/helpers/dimensionHelper'
import OrderHelper from '@glass-designer/helpers/orderHelper'
import GuidelineHelper from '@glass-designer/helpers/guidelineHelper'

import RectangleShape from '@glass-designer/model/shapes/rectangleShape'
import CircleShape from '@glass-designer/model/shapes/circleShape'
import OvalShape from '@glass-designer/model/shapes/ovalShape'
import RectangleHole from '@glass-designer/model/holes/rectangleHole'
import CircleHole from '@glass-designer/model/holes/circleHole'
import OvalHole from '@glass-designer/model/holes/ovalHole'
import CustomHole from '@glass-designer/model/holes/customHole'
import DatumLink from '@glass-designer/model/datumLink'
import GlassSegment from '@/model/glassSegment'

// Action tools
import SelectTool from '@glass-designer/tools/action/selectTool'
import CutTool from '@glass-designer/tools/action/cutTool'
import MeasureTool from '@glass-designer/tools/action/measureTool'
import SkewTool from '@glass-designer/tools/action/skewTool'
import SplitTool from '@glass-designer/tools/action/splitTool'
import ShapeTool from '@glass-designer/tools/action/shapeTool'
import EdgeCutoutTool from '@glass-designer/tools/action/edgeCutoutTool'

// Library tools
import AddGlassShapeTool from '@glass-designer/tools/library/addGlassShapeTool'
import AddCenterCutoutTool from '@glass-designer/tools/library/addCenterCutoutTool'
import AddEdgeCutoutTool from '@glass-designer/tools/library/addEdgeCutoutTool'
import AddCornerCutoutTool from '@glass-designer/tools/library/addCornerCutoutTool'
import AddPanelItemTool from '@glass-designer/tools/library/addPanelItemTool'

// Integrated tools
import DimensionEditTool from '@glass-designer/tools/integrated/dimensionEditTool'
import DimensionEditDatumTool from '@glass-designer/tools/integrated/dimensionEditDatumTool'
import SegmentMoveTool from '@glass-designer/tools/integrated/segmentMoveTool'
import CenterCutoutMoveTool from '@glass-designer/tools/integrated/centerCutoutMoveTool'
import ItemMoveTool from '@glass-designer/tools/integrated/itemMoveTool'
import SegmentAddPointsTool from '@glass-designer/tools/integrated/segmentAddPointsTool'
import ShapeExpandShrinkTool from '@glass-designer/tools/integrated/shapeExpandShrinkTool'

import { OrderTypes, InventoryStockTypes, OrderEditorEdgeworkSettingTypes, PriceTypes, UOMTypes, AccountSettingKeys } from '@/store/types'
import GlassDesignerTypes, { ToolTypes } from '@glass-designer/types'
import { i, im } from 'mathjs'



export default class GlassEditor {
  constructor(component, renderer, scene, element) {
    this.component = component
    this.renderer = renderer
    this.scene = scene
    this.element = element
    this.editorLoaded = false
    this.camera = null

    this.lastTouchPosition = new THREE.Vector2()
    this.mousePosition = new THREE.Vector2()
    this.touchPosition = new THREE.Vector2()
    this.raycaster = new THREE.Raycaster()
    this.snapRaycaster = new THREE.Raycaster()
    this.mouseUnproject = new THREE.Vector3()
    this.snapUnproject = new THREE.Vector3()
    this.cameraTarget = new THREE.Vector3()

    this.dimensionHelper = new DimensionHelper(this)
    this.guidelineHelper = new GuidelineHelper(this)

    this.viewLevel = GlassDesignerTypes.EditorViewLevelTypes.normal

    this.settings = {
      // Internal configuration settings
      maxWorldWidth: 40000,
      maxWorldHeight: 40000,
      viewportWidth: element.clientWidth,
      viewportHeight: element.clientHeight,
      isTouchDevice: false,
      maxUndoHistory: 50,
      mouseRadius: 2.5,   // In screen coordinates
      touchRadius: 10.0,   // In screen coordinates
      isEditingMode: true,
      isReviewConfigEnabled: true,

      // Theme in use. Copied from theme data in settings for the theme selected
      theme: {},

      // User configuration settings
      userConfig: {
        themeId: 'light',
        appGridEnabled: true,
        appRulersEnabled: true,
        linearDimensionPrecision: 1,
        angularDimensionPrecision: 2,
        displayLinearDimensionUnits: false,
        displayAngularDimensionUnits: true,
        displayDimensionTrailingZeros: true
      }
    }

    // Populate the built-in library items
    this.loadLibraryItems()

    // Load user config
    this.loadUserConfig()

    // Configure theme
    this.settings.theme = _.cloneDeep(Config.Theme[this.settings.userConfig.themeId])
    this.scene.background = new THREE.Color(this.settings.theme.background)
    this.component.updateUserConfig(this.settings.userConfig, this.settings.theme)

    this.resources = {
      vertexTextureHandle: null,
      vertexTexture: null,
      vertexTextureShapeSelected: null,
      arrowTexture: null,
      arrowTextureRotate: null,
      arrowTextureMoveHorz: null,
      arrowTextureMoveAll: null,
      iconWarningTexture: null,
      iconApprovedTexture: null,
      iconTextureBG: null,
      iconTextureFill: null,
      panelGrainTextureVertical: null,
      panelGrainTextureHorizontal: null,
      panelCathedralTexture: null,
      panelSatinliteTexture: null,
      panelSpotswoodTexture: null,

      orderStampImageTexture: null,

      // Textures for the library items (panel item types)
      // Added here, with key's being the OrderEditorLibraryItem.id and value the Three.Texture object
      libraryItemTextures: {}
    }

    // Texture cache used for gradients
    this.textureCache = {}

    this.configurationData = {
      stockList: [],
      cutoutList: [],
      edgeworkList: [],
      defaultEdgeworkList: [],
      orderEditorSettings: {},
      orderEditorGuidelines: [],
      applyDefaultPanelItems: [],
      orderStampConfig: {},
      stockPriceGroupList: [],
      shapePriceTypeList: [],
      salesTaxRate: {},
      accountSettings: {}
    }

    // Detect touch device
    this.settings.isTouchDevice = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0))

    // JB: 10/01/2022: Chrome now showing up as touch device, so manually disabling
    this.settings.isTouchDevice = false

    // TEMP while developing
    //this.settings.isTouchDevice = true

    // If an object is capture, it receives all mouse/touch move events
    this.eventCapturedObject = null
    this.mouseOverObject = null
    this.mouseCapturedDownEvent = false

    // Main order data structure
    // Contains order & customer info
    this.editorOrder = new Order()

    this.shapeList = []
    this.objectList = []
    this.undoGroupList = []
    this.currentUndoGroup = null
    this.selectionList = []
    this.toolList = []

    // Viewport settings
    this.zoomScale = 1.0
    this.zoomStep = this.calculateZoomStepFromScale(this.zoomScale)
    this.originOffsetX = 0
    this.originOffsetY = 0

    this.shapeSelectionEnabled = true
    this.dimensionTypesEnabled = [GlassDesignerTypes.DimensionTypes.all]
    this.shapeEditingMode = false
    this.optionDimensionsVisible = true
    this.optionEdgeworkVisible = true
    this.optionWarningsVisible = true
    this.optionSnapEnabled = true
    this.optionSnapGridEnabled = false

    this.mouseCursor = 'default'

    // Setup a timer to render if required
    // We DO NOT want to render at full animation speeds (eg, 60fps) as we really don't require that
    // This is just a check if the editor has been flagged for rendering to render the next available moment
    let _editor = this
    this.renderTimer = setInterval(() => {
      _editor.checkRequiresRender()
    }, 100)
  }


  /**
   * Release all allocated objects & cleanup for closing the editor
   */
  dispose() {
    if(this.renderTimer) {
      clearInterval(this.renderTimer)
      this.renderTimer = null
    }

    try {
      // Remove all objects from the scene & dispose them
      var objectListCopy = [...this.objectList]
      this.objectList = []
      objectListCopy.forEach(obj => {
        obj.dispose()
      })

      // Dispose textures
      Object.keys(this.resources).forEach(textureField => {
        if(this.resources[textureField] instanceof THREE.Texture) {
          this.resources[textureField].dispose()
          this.resources[textureField] = null
        }
      })
      Object.keys(this.textureCache).forEach(textureField => {
        if(this.textureCache[textureField] instanceof THREE.Texture) {
          this.textureCache[textureField].dispose()
          this.textureCache[textureField] = null
        }
      })
      this.textureCache = {}

      // Library panel item textures
      if(this.resources.libraryItemTextures) {
        Object.keys(this.resources.libraryItemTextures).forEach(textureField => {
          let tex = this.resources.libraryItemTextures[textureField]
          if(tex instanceof THREE.Texture) {
            tex.dispose()
          }
        })
      }
      this.resources.libraryItemTextures = {}


      // Dispose tools
      if(this.currentTool) {
        this.scene.remove(this.currentTool.sceneObject)
        this.currentTool = null
      }
      this.toolList.forEach(tool => {
        tool.dispose()
      })
      this.toolList = []

    
      // Dispose other global objects
      if(this.grid) {
        this.grid.dispose()
        this.grid = null
      }

      if(this.moveHandle) {
        this.moveHandle.dispose()
        this.moveHandle = null
      }
      if(this.reviewHighlightObj) {
        this.reviewHighlightObj.dispose()
        this.reviewHighlightObj = null
      }

      if(this.renderer) {
        this.renderer.dispose()
        this.renderer = null
      }

    } catch(err) {
      console.log(`Error disposing editor`)
      console.error(err)
    }
  }


  /**
   * Load all the editor resources & settings
   */
  loadEditor() {
    return new Promise(async (resolve, reject) => {
      try {
        await this.loadFonts()
        this.loadTexturesSync()

        // Load all the configuration details from the server (cutouts, edgeworks, editor config)
        await this.loadConfigurationData()

        // Highlight shape available for editing mode
        this.highlightShape = null

        // Grid
        this.grid = new GlassGrid(this)
        this.grid.updateSceneObject()

        // Create the tools
        // Action Tools
        this.addTool(new SelectTool(this))
        this.addTool(new CutTool(this))
        this.addTool(new MeasureTool(this))
        this.addTool(new SkewTool(this))
        this.addTool(new SplitTool(this))
        this.addTool(new ShapeTool(this))
        this.addTool(new EdgeCutoutTool(this))

        // Library Tools
        this.addTool(new AddGlassShapeTool(this))
        this.addTool(new AddCenterCutoutTool(this))
        this.addTool(new AddEdgeCutoutTool(this))
        this.addTool(new AddCornerCutoutTool(this))
        this.addTool(new AddPanelItemTool(this))

        // Integrated Tools
        this.addTool(new DimensionEditTool(this))
        this.addTool(new DimensionEditDatumTool(this))
        this.addTool(new SegmentMoveTool(this))
        this.addTool(new CenterCutoutMoveTool(this))
        this.addTool(new ItemMoveTool(this))
        this.addTool(new SegmentAddPointsTool(this))
        this.addTool(new ShapeExpandShrinkTool(this))

        this.startTool(GlassDesignerTypes.ToolTypes.selectTool)


        // Create editing objects
        this.moveHandle = new EditingMoveGroupHandle(this)
        this.moveHandle.setVisible(false)
        this.moveHandle.setObjectEventListener(this)
        this.scene.add(this.moveHandle.getSceneObject())

        this.reviewHighlightObj = new ReviewHighlightObject(this)
        this.reviewHighlightObj.setVisible(false)
        this.scene.add(this.reviewHighlightObj.getSceneObject())

        // Recalculate dimensions
        this.rebuildAllDimensions()

        // Rerun guideline inspection
        this.guidelineHelper.runFullGuidelineInspection()
        this.component.updateWarningList(this.guidelineHelper.getWarningList())
        this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

        // Go into normal view level
        this.setViewLevelType(GlassDesignerTypes.EditorViewLevelTypes.normal)

        // Finish loading
        this.editorLoaded = true
        this.render()

        return resolve()
      } catch(err) {
        return reject(err)
      }
    })
  }


  /**
   * Set the view level type for the editor
   */
  setViewLevelType(viewLevel) {
    var previousLevel = this.viewLevel
    this.viewLevel = viewLevel

    var cameraUpdated = false

    // Show/Hide stuff based on view level
    if(this.viewLevel != GlassDesignerTypes.EditorViewLevelTypes.view3D) {
      // Editing
      this.grid.setVisible(this.getUserConfig().appGridEnabled)
      this.setOptionDisplayDimensions(this.optionDimensionsVisible)
      this.setOptionDisplayEdgework(this.optionEdgeworkVisible)
      this.setOptionDisplayWarnings(this.optionWarningsVisible)

      // Reset to ortho camera if we previously had a perspective camera
      if(previousLevel == GlassDesignerTypes.EditorViewLevelTypes.view3D || this.camera == null) {
        this._createOrthoCameraView()
        cameraUpdated = true
      }

    } else {
      // 3D Viewing
      this.grid.setVisible(true)
      
      this.objectList.forEach(object => {
        if(object instanceof BaseDimension) {
          if(!object.isReleasedDimension)
            object.setVisible(false)
        }
      })

      // Reset to perspective camera
      this._createPerspectiveCameraView()
      cameraUpdated = true
    }

    // Update editing mode for objects
    this.objectList.forEach(object => {
      object.viewLevelUpdated(this.viewLevel)
    })

    // Update warnings
    this.guidelineHelper.viewLevelUpdated(this.viewLevel)

    // Show/Hide various objects for view level change
    this.hideReviewHighlightRegion()

    // Rebuild dimensions
    // Different view types will have different dimensions
    this.rebuildAllDimensions()

    if(cameraUpdated)
      this.moveCameraToAllGlassShapes()

    // Default to selection tool
    this.startTool(GlassDesignerTypes.ToolTypes.selectTool)

    this.render()
  }


  /**
   * Get the editor view level type
   */
  getViewLevel() {
    return this.viewLevel
  }


  /**
   * Get if the editor is in selection mode
   */
  isSelectEnabled() {
    return this.viewLevel == GlassDesignerTypes.EditorViewLevelTypes.normal
  }


  /**
   * Update the order / contact details for the order
   */
  updateOrder(orderData) {
    this.editorOrder.updateDetails(_.cloneDeep(orderData))

    // Load the order item list & create all the shapes from the order item list
    // Order items not created by the editor are ignored
    if(_.isArray(orderData.orderItems)) {
      this.loadFromOrderItemList(orderData.orderItems)

      this.resetView()
    }
  }


  /**
   * Get the order data object
   */
  getOrder() {
    return this.editorOrder
  }


  /**
   * Generate the shapes from the order items list
   * Order items not created by the editor are ignored
   */
  loadFromOrderItemList(orderItemList) {
    let shapeItems = orderItemList.filter(orderItem => {
      if(orderItem.stockType != InventoryStockTypes.glass && orderItem.stockType != InventoryStockTypes.assembly)
        return false
      if(!orderItem.isEditorGeneratedItem)
        return false
      if(!_.isObject(orderItem.editorData) || !_.isObject(orderItem.editorData.data))
        return false

      // Ignore child glass/assembly items
      // They'll get deserialized by the parent object
      if(orderItem.parentOrderItemId != null)
        return false

      // Note: In future can do version checks for backwards compatibility
      //let storedVersion = parseInt(orderItem.editorData.version || 0)

      return true
    })

    // Convert top level glass order items to shapes
    shapeItems.forEach(shapeOrderItem => {
      let storedData = shapeOrderItem.editorData.data

      try {
        var newShape = EditorFactory.createObject(storedData.class, this)
        newShape.deserialize(storedData)

        // Update details from the order item
        newShape.updateOrderItemData(shapeOrderItem, orderItemList)

        // When loading, flag the glass items as automatically approved until modified
        // Only apply during editing & if review is flagged
        if(this.settings.isEditingMode && this.settings.isReviewConfigEnabled) {
          newShape.reviewApprovalOverride = true

          // Also apply to all children (for assemblies)
          if(newShape instanceof AssemblyGroup) {
            newShape.getChildObjectList().forEach(childObj => {
              childObj.reviewApprovalOverride = true
            })
          }
        }

        newShape.updateSceneObject()

      } catch(err) {
        console.error(err)
      }
    })

    this.shapeListUpdated()
    this.clearSelection()
    this.rebuildAllDimensions()

    // Rerun guideline inspection
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    // Force a render
    this.render()
  }


  /**
   * Reupdate the order item list for the entire order
   * Called when order is saved / displayed
   * 
   * Returns a promise resolved when the order item list is updated
   */
  updateOrderItemList() {
    let orderHelper = new OrderHelper(this)
    var glassShapeList = this.getShapeList()
    var assemblyGroupList = this.getAssemblyGroupList()

    return orderHelper.updateOrderItemList(this.editorOrder, glassShapeList, assemblyGroupList, this.configurationData)
  }


  /**
   * Get the dimension helper
   */
  getDimensionHelper() {
    return this.dimensionHelper
  }


  /**
   * Get the guideline helper
   */
  getGuidelineHelper() {
    return this.guidelineHelper
  }


  /**
   * Get the editor user configuration details
   */
  getUserConfig() {
    return this.settings.userConfig
  }


  /**
   * Load user config from local storage
   */
  loadUserConfig() {
    var storage = window.localStorage
    try {
      let storeConfig = JSON.parse(storage.getItem('orderEditingUserConfig')) || {}

      _.merge(this.settings.userConfig, storeConfig)

      this.settings.theme = _.cloneDeep(Config.Theme[this.settings.userConfig.themeId])
    } catch(err) {
    }
  }


  /**
   * Update the user config details
   */
  updateUserConfig(config) {
    var storage = window.localStorage

    _.merge(this.settings.userConfig, config)
    storage.setItem('orderEditingUserConfig', JSON.stringify(this.settings.userConfig))

    // Update theme
    this.settings.theme = _.cloneDeep(Config.Theme[this.settings.userConfig.themeId])
    this.scene.background = new THREE.Color(this.settings.theme.background)
    this.component.updateUserConfig(this.settings.userConfig, this.settings.theme)

    // Rebuild & re-render everything
    this.grid.setVisible(this.getUserConfig().appGridEnabled)
    this.grid.updateSceneObject()
    
    // Clear state
    this.cancelActiveTool()
    this.clearSelection()
    this.rebuildAllDimensions()

    // Rebuild shape objects
    this.objectList.forEach(object => {
      if (object instanceof GlassShape) {
        if (object.shapeHighlightOnly)
          return

        object.updateSceneObject()
      }
    })

    this.render()
  }


  /**
   * Get the theme info
   */
  getTheme() {
    return this.settings.theme
  }


  /**
   * Get/Set the editing mode
   */
  isEditingAvailable() {
    return this.settings.isEditingMode
  }
  setEditingAvailable(editingEnabled) {
    this.settings.isEditingMode = editingEnabled
  }

  /**
   * Set the review mode enabled from settings
   */
  setReviewConfigEnabled(reviewConfigEnabled) {
    this.settings.isReviewConfigEnabled = reviewConfigEnabled
  }




  // ------------------- Events -------------------


  onMouseWheelEvent(ev) {
    if(!this.editorLoaded)
      return

    let mouseData = this.calculateMouseProjection(ev)

    this.currentTool.onMouseWheelEvent(ev, mouseData)

    this.objectList.forEach(obj => {
      if (obj.listenMouseEvents && obj.isVisible() && obj.isEnabled()) {
        if(!this.isObjectSelectable(obj))
          return

        obj.onMouseWheelEvent(ev, mouseData)
      }
    })

    this.onCameraUpdated()

    return mouseData
  }
  onMouseDownEvent(ev) {
    if(!this.editorLoaded)
      return

    this.mouseCapturedDownEvent = true

    let mouseData = this.calculateMouseProjection(ev)
    var objectProcessedEvent = false

    var objList = this.objectList.filter(obj => obj.listenMouseEvents && obj.isVisible() && obj.isEnabled())
    objList = objList.filter(obj => this.isObjectSelectable(obj))
    let closestObjectInfoToRay = this.findClosestObjectToRay(objList, mouseData.raycaster.ray, mouseData.radius)

    if(ev.button == Config.Mouse.buttonSelect) {
      this.releaseCapturedObject()

      if(closestObjectInfoToRay) {
        closestObjectInfoToRay.object.onMouseDownEvent(ev, mouseData, closestObjectInfoToRay)
      }
    }

    if(!this.eventCapturedObject)
      this.currentTool.onMouseDownEvent(ev, mouseData)

    if (this.requiresRender)
      this.render()

    return mouseData
  }
  onMouseMoveEvent(ev) {
    if(!this.editorLoaded)
      return

    let mouseData = this.calculateMouseProjection(ev)

    // Reste hover settings
    this.mouseCursor = 'default'
    this.element.style.cursor = this.mouseCursor
    
    let prevMouseOverObject = this.mouseOverObject
    this.mouseOverObject = null

    if (this.eventCapturedObject) {
      this.eventCapturedObject.onMouseMoveEvent(ev, mouseData, null)
      this.mouseOverObject = this.eventCapturedObject

    } else {
      let toolProcessedEvent = this.currentTool.onMouseMoveEvent(ev, mouseData)

      if(!toolProcessedEvent) {
        var objList = this.objectList.filter(obj => obj.listenMouseEvents && obj.isVisible() && obj.isEnabled())
        objList = objList.filter(obj => this.isObjectSelectable(obj))

        let closestObjectInfoToRay = this.findClosestObjectToRay(objList, mouseData.raycaster.ray, mouseData.radius)

        if (closestObjectInfoToRay) {
          if(prevMouseOverObject != closestObjectInfoToRay.object)
            closestObjectInfoToRay.object.onMouseEnterEvent(ev, mouseData)

          closestObjectInfoToRay.object.onMouseMoveEvent(ev, mouseData, closestObjectInfoToRay)

          this.mouseOverObject = closestObjectInfoToRay.object
        }
      }
    }

    if(prevMouseOverObject && this.mouseOverObject != prevMouseOverObject)
      prevMouseOverObject.onMouseLeaveEvent(ev, mouseData)

    if (this.requiresRender)
      this.render()

    return mouseData
  }
  onMouseUpEvent(ev) {
    if(!this.editorLoaded)
      return

    // Ignore event if mouse down did not start on the editor
    // Eg, mouse down over popover/dialog/UI
    if(!this.mouseCapturedDownEvent)
      return

    this.mouseCapturedDownEvent = false

    let mouseData = this.calculateMouseProjection(ev)

    if (this.eventCapturedObject) {
      this.eventCapturedObject.onMouseUpEvent(ev, mouseData, null)

    } else {
      let toolProcessedEvent = this.currentTool.onMouseUpEvent(ev, mouseData)

      if (ev.button == Config.Mouse.buttonSelect && !toolProcessedEvent) {
        var objList = this.objectList.filter(obj => obj.listenMouseEvents && obj.isVisible() && obj.isEnabled())
        objList = objList.filter(obj => this.isObjectSelectable(obj))

        let closestObjectInfoToRay = this.findClosestObjectToRay(objList, mouseData.raycaster.ray, mouseData.radius)

        if (closestObjectInfoToRay) {
          // Hide any active popovers / selection
          //this.component.hidePopover()
          //this.clearSelection()

          closestObjectInfoToRay.object.onMouseUpEvent(ev, mouseData, closestObjectInfoToRay)
        }
      }
    }

    if (this.requiresRender)
      this.render()

    return mouseData
  }

  // Touch Processing
  onTouchStartEvent(ev) {
    if(!this.editorLoaded)
      return

    let touchData = this.calculateTouchProjection(ev)
    this.currentTool.onTouchStartEvent(ev, touchData)

    this.releaseCapturedObject()

    // Note: Send as mouse events to simplify code

    var objList = this.objectList.filter(obj => obj.listenMouseEvents && obj.isVisible() && obj.isEnabled())
    objList = objList.filter(obj => this.isObjectSelectable(obj))

    let closestObjectInfoToRay = this.findClosestObjectToRay(objList, touchData.raycaster.ray, touchData.radius)

    if (closestObjectInfoToRay) {
      closestObjectInfoToRay.object.onMouseDownEvent(ev, touchData, closestObjectInfoToRay)
    }

    if (this.requiresRender)
      this.render()

    return touchData
  }
  onTouchEndEvent(ev) {
    if(!this.editorLoaded)
      return

    let touchData = this.calculateTouchProjection(ev)

    if (!this.eventCapturedObject)
      this.currentTool.onTouchEndEvent(ev, touchData)

    // Note: Send as mouse events to simplify code

    if (ev.touches.length == 0) {

      if (this.eventCapturedObject) {
        this.eventCapturedObject.onMouseUpEvent(ev, touchData, {})

        } else {

        var objList = this.objectList.filter(obj => obj.listenMouseEvents && obj.isVisible() && obj.isEnabled())
        objList = objList.filter(obj => this.isObjectSelectable(obj))

        let closestObjectInfoToRay = this.findClosestObjectToRay(objList, touchData.raycaster.ray, touchData.radius)

        if (closestObjectInfoToRay) {
          closestObjectInfoToRay.object.onMouseUpEvent(ev, touchData, closestObjectInfoToRay)
        }
      }
    }

    if (this.requiresRender)
      this.render()

    return touchData
  }
  onTouchCancelEvent(ev) {
    if(!this.editorLoaded)
      return

    let touchData = this.calculateTouchProjection(ev)
    this.currentTool.onTouchCancelEvent(ev, touchData)

    if (this.requiresRender)
      this.render()

    return touchData
  }
  onTouchMoveEvent(ev) {
    if(!this.editorLoaded)
      return

    let touchData = this.calculateTouchProjection(ev)

    if (!this.eventCapturedObject)
      this.currentTool.onTouchMoveEvent(ev, touchData)

    // Note: Send as mouse events to simplify code

    if (this.eventCapturedObject) {
      this.eventCapturedObject.onMouseMoveEvent(ev, touchData, {})

    } else {
      this.objectList.forEach(obj => {
        if (obj.listenMouseEvents && obj.isVisible() && obj.isEnabled())
          obj.onMouseMoveEvent(ev, touchData, {})
      })
    }

    if (this.requiresRender)
      this.render()

    return touchData
  }

  onKeyDownEvent(ev) {
    // Ctrl / Cmd actions
    if (ev.metaKey || ev.ctrlKey) {
        
      switch(ev.key) {
        // Cmd-Z / Ctrl-Z : Undo last action
        case 'z':
          this.undoLastAction()
          return
      }
    }

    this.currentTool.onKeyDownEvent(ev)
  }

  onCameraUpdated() {
    this.originOffsetX = this.camera.position.x * this.zoomScale
    this.originOffsetY = this.camera.position.y * this.zoomScale

    this.render()
  }

  updateViewport(width, height) {
    if (width == 0 || height == 0)
      return
    var viewSize = height
    var aspectRatio = width / height

    var viewport = {
      viewSize: viewSize,
      aspectRatio: aspectRatio,
      left: (-aspectRatio * viewSize) / 2,
      right: (aspectRatio * viewSize) / 2,
      top: -viewSize / 2,
      bottom: viewSize / 2,
      near: -500,
      far: 500
    }

    if(this.camera) {
      this.camera.left = viewport.left
      this.camera.right = viewport.right
      this.camera.top = viewport.top
      this.camera.bottom = viewport.bottom
      this.camera.near = viewport.near
      this.camera.far = viewport.far
      this.camera.updateProjectionMatrix()
    }

    this.renderer.setSize(width, height)

    this.settings.viewportWidth = width
    this.settings.viewportHeight = height

    this.render()
  }


  /**
   * Create the orthographic camera for editing mode
   */
  _createOrthoCameraView() {
    var viewSize = this.settings.viewportHeight
    var aspectRatio = this.settings.viewportWidth / this.settings.viewportHeight

    var _viewport = {
      viewSize: viewSize,
      aspectRatio: aspectRatio,
      left: (-aspectRatio * viewSize) / 2,
      right: (aspectRatio * viewSize) / 2,
      top: -viewSize / 2,
      bottom: viewSize / 2,
      near: -500,
      far: 500
    }

    this.camera = new THREE.OrthographicCamera ( 
      _viewport.left, 
      _viewport.right, 
      _viewport.top, 
      _viewport.bottom, 
      _viewport.near, 
      _viewport.far 
    )
    this.camera.updateProjectionMatrix()
  }


  /**
   * Create the perspective camera for 3D viewing mode
   */
  _createPerspectiveCameraView() {
    let fieldOfView = 45.0
    let nearPlane = 1.0
    let farPlane = 15000.0
    var aspectRatio = this.settings.viewportWidth / this.settings.viewportHeight

    let defaultDistance = 5000
    var defaultOffset = new THREE.Vector3(defaultDistance, -defaultDistance, defaultDistance * 0.5)

    this.cameraTarget.set(0, 0, 0)

    this.camera = new THREE.PerspectiveCamera(fieldOfView, aspectRatio, nearPlane, farPlane)
    this.camera.updateProjectionMatrix()
    this.camera.up.set(0, 0, 1.0)
    this.camera.position.copy(defaultOffset)
    this.camera.lookAt(this.cameraTarget)
  }


  /**
   * Calculate the world viewing 2D bounds
   * Valid for orthographic camera only
   */
  calculateWorldViewBounds() {
    const dx = ( this.camera.right - this.camera.left ) / ( 2 * this.camera.zoom )
    const dy = ( this.camera.top - this.camera.bottom ) / ( 2 * this.camera.zoom )
    const cx = ( this.camera.right + this.camera.left ) / 2
    const cy = ( this.camera.top + this.camera.bottom ) / 2

    let left = cx - dx + this.camera.position.x
    let right = cx + dx + this.camera.position.x
    let top = cy + dy + this.camera.position.y
    var bottom = cy - dy + this.camera.position.y

    return new THREE.Box2(new THREE.Vector2(left, top), new THREE.Vector2(right, bottom))
  }


  /**
   * Main rendering process
   */
  render() {
    if(!this.editorLoaded)
      return

    this.objectList.forEach(object => {
      if (object.isVisible())
        object.onBeforeRender()
    })

    if (this.currentTool)
      this.currentTool.onBeforeRender()

    this.renderer.render(this.scene, this.camera)

    this.requiresRender = false
  }


  /**
   * Check if a render has been flagged
   */
  checkRequiresRender() {
    if(this.requiresRender)
      this.render()
  }



  /**
   * Load the fonts used by the editor
   * Returns a promise resolved on completion
   */
  loadFonts() {
    return new Promise((resolve, reject) => {
      try {

        // No longer used
        // TODO: Maybe precache the ttf file in the new font lib

        return resolve()

      } catch (err) {
        return reject(err)
      }
    })
  }


  /**
   * Load the order editor configuration details
   * Returns a promise resolved on completion
   */
  loadConfigurationData() {
    return new Promise(async (resolve, reject) => {
      try {

        let results = await Promise.all([
          this.component.$apiService.inventoryService.getStockList({ include: 'stockSalesPrice' }),
          this.component.$apiService.glassDesignerService.getOrderEditorCutoutList(),
          this.component.$apiService.glassDesignerService.getOrderEditorEdgeworkList({ include: 'orderEditorEdgeworkStock,orderEditorEdgeworkAllowance,orderEditorEdgeworkStockAllowance' }),
          this.component.$apiService.glassDesignerService.getOrderEditorDefaultEdgeworkList(),
          this.component.$apiService.glassDesignerService.getOrderEditorShapePriceTypeList(),
          this.component.$apiService.glassDesignerService.getConfigSettings(),
          this.component.$apiService.glassDesignerService.getOrderEditorGuidelines(),
          this.component.$apiService.glassDesignerService.getLibraryItems({}, { type: GlassDesignerTypes.LibraryItemTypes.panelItem, applyDefault: 1 }),
          this.component.$apiService.ordersService.getStampConfig(),
          this.component.$apiService.pricingService.getStockPriceGroupList(),
          this.component.$apiService.adminService.getAccount(),
          this.component.$apiService.adminService.getAccountSettings()
        ])

        this.configurationData.stockList = results[0].data
        this.configurationData.cutoutList = results[1].data
        this.configurationData.edgeworkList = results[2].data
        this.configurationData.defaultEdgeworkList = results[3].data
        this.configurationData.shapePriceTypeList = results[4].data
        this.configurationData.orderEditorSettings = results[5].data
        this.configurationData.orderEditorGuidelines = results[6].data
        this.configurationData.applyDefaultPanelItems = results[7].data
        this.configurationData.orderStampConfig = results[8].data

        this.configurationData.stockPriceGroupList = results[9].data
        let accountInfo = results[10].data
        this.configurationData.accountSettings = results[11].data
        
        this.configurationData.salesTaxRate = accountInfo.salesTaxRate

        return resolve()
      } catch (err) {
        return reject(err)
      }
    })
  }


  /**
   * Get the configuration data object
   * Contains all the loaded config
   */
  getEditorConfigurationData() {
    return this.configurationData
  }


  /**
   * Load the library items for the editor
   */
  loadLibraryItems() {
    // Populate the built in library item list
    this.component.$store.commit('apps/editorLibrary/clearLibrary')
    this.populateBuiltInLibraryItems()
  }


  /**
   * Populate the editor library with default items for the standard list
   */
  populateBuiltInLibraryItems() {
    // Shapes
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.shape,
      name: 'Circle',
      icon: 'fa-circle',
      itemData: {
        shapeList: [{
          shapeData: {
            shapeType: GlassDesignerTypes.LibraryItemShapeTypes.circle,
            radius: 500
          }
        }]
      }
    })
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.shape,
      name: 'Rectangle',
      icon: 'fa-square-full',
      itemData: {
        shapeList: [{
          shapeData: {
            shapeType: GlassDesignerTypes.LibraryItemShapeTypes.rectangle,
            width: 1000,
            height: 1000
          }
        }]
      }
    })
    /*
    Enable when fully supported & working
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.shape,
      name: 'Semi Circle',
      icon: 'fa-circle',
      itemData: {
        shapeList: [{
          shapeData: {
            shapeType: GlassDesignerTypes.LibraryItemShapeTypes.semiCircle,
            radius: 500
          }
        }]
      }
    })*/
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.shape,
      name: 'Oval',
      icon: 'fa-circle',
      iconClass: 'gd-library-icon-oval',
      itemData: {
        shapeList: [{
          shapeData: {
            shapeType: GlassDesignerTypes.LibraryItemShapeTypes.oval,
            width: 1000,
            height: 750
          }
        }]
      }
    })

    // Assemblies
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.assembly,
      name: 'Rectangle',
      icon: 'fa-square-full',
      itemData: {
        shapeList: [{
          shapeData: {
            shapeType: GlassDesignerTypes.LibraryItemShapeTypes.rectangle,
            width: 1000,
            height: 1000
          }
        }]
      }
    })

    // Center Cutouts
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.centerCutOut,
      name: 'Circle',
      icon: 'fa-circle',
      itemData: {
        cutoutList: [{
          cutoutData: {
            holeType: GlassDesignerTypes.LibraryItemShapeTypes.circle,
            radius: 5
          }
        }]
      }
    })
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.centerCutOut,
      name: 'Rectangle',
      icon: 'fa-square-full',
      itemData: {
        cutoutList: [{
          cutoutData: {
            holeType: GlassDesignerTypes.LibraryItemShapeTypes.rectangle,
            width: 100,
            height: 60,
          }
        }]
      }
    })
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.centerCutOut,
      name: 'Oval',
      icon: 'fa-circle',
      iconClass: 'gd-library-icon-oval',
      itemData: {
        cutoutList: [{
          cutoutData: {
            holeType: GlassDesignerTypes.LibraryItemShapeTypes.oval,
            width: 100,
            height: 60,
          }
        }]
      }
    })

    // Edge Cutouts
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.edgeCutOut,
      name: 'Circle',
      icon: 'fa-circle',
      itemData: {
        cutoutList: [{
          cutoutData: {
            shapeType: GlassDesignerTypes.LibraryItemShapeTypes.circle,
            radius: 20
          }
        }]
      }
    })
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.edgeCutOut,
      name: 'Rectangle',
      icon: 'fa-square-full',
      itemData: {
        cutoutList: [{
          cutoutData: {
            shapeType: GlassDesignerTypes.LibraryItemShapeTypes.rectangle,
            width: 80,
            height: 44
          }
        }]
      }
    })

    // Corner Cutouts
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.cornerCutOut,
      name: 'Circle',
      icon: 'fa-circle',
      itemData: {
        cutoutList: [{
          cutoutData: {
            shapeType: GlassDesignerTypes.LibraryItemShapeTypes.circle,
            radius: 20
          }
        }]
      }
    })
    this.component.$store.commit('apps/editorLibrary/addBuiltInLibraryItem', {
      id: uuidv4(),
      isCoreItem: true,
      type: GlassDesignerTypes.LibraryItemTypes.cornerCutOut,
      name: 'Rectangle',
      icon: 'fa-square-full',
      itemData: {
        cutoutList: [{
          cutoutData: {
            shapeType: GlassDesignerTypes.LibraryItemShapeTypes.rectangle,
            width: 80,
            height: 44
          }
        }]
      }
    })
  }

  setOptionDisplayDimensions(displayDimensions) {
    this.optionDimensionsVisible = displayDimensions

    let isAllEnabled = this.dimensionTypesEnabled.indexOf(GlassDesignerTypes.DimensionTypes.all) != -1

    // Toggle dimension object 
    this.objectList.forEach(object => {
      if (object instanceof BaseDimension) {
        // Ignore dimensions released by dimension helper. They're internally kept hidden
        if(object.isReleasedDimension)
          return

        let isDimEnabled = this.dimensionTypesEnabled.indexOf(object.dimType) != -1

        object.setVisible(this.optionDimensionsVisible && (isDimEnabled || isAllEnabled))
      }
    })

    this.render()
  }
  setOptionDisplayEdgework(displayEdgework) {
    this.optionEdgeworkVisible = displayEdgework

    // Rebuild the glass shapes
    this.getShapeList().forEach(shape => {
      shape.updateSceneObject()
    })

    this.render()
  }
  getOptionDisplayEdgework() {
    return this.optionEdgeworkVisible
  }
  setOptionDisplayWarnings(displayWarnings) {
    this.optionWarningsVisible = displayWarnings

    this.guidelineHelper.displayWarningsOptionUpdated()    

    this.render()
  }
  getOptionDisplayWarnings() {
    return this.optionWarningsVisible
  }  
  setOptionSnapEnabled(snapEnabled) {
    this.optionSnapEnabled = snapEnabled
  }
  getOptionSnapEnabled() {
    return this.optionSnapEnabled
  }
  setOptionSnapGridEnabled(snapGridEnabled) {
    this.optionSnapGridEnabled = snapGridEnabled
  }
  getOptionSnapGridEnabled() {
    return this.optionSnapGridEnabled
  }
  
  resetView() {
    this.moveCameraToAllGlassShapes()
  }


  /**
   * Get the list of shapes
   */
  getShapeList() {
    return this.getObjectListOfType(GlassShape).filter(shape => !shape.shapeHighlightOnly)
  }


  /**
   * Get the list of assembly groups
   */
  getAssemblyGroupList() {
    return this.getObjectListOfType(AssemblyGroup)
  }


  /**
   * Get the list of valid shapes for review
   * Ignores cancelled / NCR hidden shapes
   */
  getValidShapeList() {
    return this.getObjectListOfType(GlassShape)
    .filter(shape => !shape.shapeHighlightOnly)
    .filter(shape => !shape.isCancelled() && !shape.isNCRLocked() && !shape.isInvoiced() && !shape.isDespatched())
  }


  /**
   * Get the list of valid assemblies for review
   * Ignores cancelled / NCR hidden assemblies
   */
  getValidAssemblyList() {
    return this.getObjectListOfType(AssemblyGroup)
    .filter(assembly => !assembly.isCancelled() && !assembly.isNCRLocked() && !assembly.isInvoiced() && !assembly.isDespatched())
  }


  /**
   * Get the list of valid shapes that are pending review
   */
  getObjectListPendingReview() {

    var shapeList = this.getValidShapeList().filter(shape => !shape.isShapeReviewApproved())
    var assemblyList = this.getValidAssemblyList().filter(assembly => !assembly.isAssemblyReviewApproved())

    return _.concat(shapeList, assemblyList)
  }

  /**
   * Find the closest glass shape to a point
   * Uses additional options to filter out shapes in the list
   */
  findClosestGlassShape(position, options = {}) {
    let testPoint = new THREE.Vector2(position.x, position.y)
    let shapeList = this.getShapeList()

    var closestShapeIndex = -1
    var closestDist = 0

    shapeList.forEach((shape, index) => {
      // Ignore stock linked panels if filter set
      if(options.ignoreStockLinked && shape.isStockLinked())
        return
      // Ignore shapes with segment list shape editing disabled if filter set
      if(options.ignoreSegmentListShapeUneditable && !shape.isSegmentListShapeEditable())
        return

      let closestInfo = shape.calculateClosestEdgePoint(testPoint, { ignoreDatumLines: true, ignoreHoles: true, ignoreSplitLines: false })
      if(closestInfo) {
        let dist = closestInfo.point.distanceTo(position)
        if(closestShapeIndex == -1 || dist < closestDist) {
          closestShapeIndex = index
          closestDist = dist
        }
      }
    })

    if(closestShapeIndex == -1)
      return null
    return shapeList[closestShapeIndex]
  }

  startShapeEditingMode(glassShape) {

    // Recreate the highlight shape to be same instance type as the editing shape
    if(this.highlightShape) {
      if(this.highlightShape.constructor.name != glassShape.constructor.name) {
        this.highlightShape.dispose()
        this.highlightShape = null
      }
    }

    if(!this.highlightShape) {
      this.highlightShape = glassShape.clone()

      // Cleanup children from highlight shape
      this.highlightShape.children.forEach(child => {
        child.dispose()
      })
      this.highlightShape.children = []
    }

    this.highlightShape.setVisible(false)
    this.highlightShape.shapeHighlightOnly = true

    this.shapeEditingMode = true
    this.highlightShape.setVisible(true)
    this.resetHighlightShape(glassShape)

    // Build all objects to display in shape editing mode
    this.objectList.forEach(object => {
      if (object instanceof GlassShape) {
        if (object.shapeHighlightOnly)
          return

        object.updateSceneObject()
      }
    })
    this.rebuildAllDimensions()


    this.render()
  }

  endShapeEditingMode() {
    if(this.shapeEditingMode) {

      this.shapeEditingMode = false
      if(this.highlightShape) {
        this.highlightShape.setVisible(false)
        this.highlightShape.segmentList = []
      }

      this.rebuildAllDimensions()
      
      // Rebuild shape objects
      this.objectList.forEach(object => {
        if (object instanceof GlassShape) {
          if (object.shapeHighlightOnly)
            return

          object.updateSceneObject()
        }
      })
      
      this.render()
    }
  }
  isShapeEditingMode() {
    return this.shapeEditingMode
  }

  resetHighlightShape(glassShape) {
    if(this.highlightShape) {
      this.highlightShape.segmentList = glassShape.segmentList.map(seg => seg.clone())
      this.highlightShape.segmentListUpdated()
      this.highlightShape.updateSceneObject()
    }
  }

  clearSelection() {
    this.getMoveHandleObject().setVisible(false)

    if (this.selectionList.length > 0) {
      this.selectionList.forEach(obj => {
        obj.clearSelection()
      })

      this.selectionList = []

      this.rebuildAllDimensions()
      this.render()
    }
  }
  getSelected() {
    return this.selectionList
  }
  setSelected(object, selectionData = {}, isSelected) {
    object.setSelected(selectionData, isSelected)
    
    if(this.selectionList.find(curObj => curObj.id == object.id) == null)
      this.selectionList.push(object)

    this.flagRenderRequired()
  }

  addTool(tool) {
    this.toolList.push(tool)
  }

  startTool(toolType, options = {}) {
    let tool = this.toolList.find(listItem => listItem.type == toolType)
    if (!tool)
      throw new Error(`Cannot find tool ${toolType}`)

    if (this.currentTool) {
      this.currentTool.onToolInactive()
      this.scene.remove(this.currentTool.sceneObject)
    }

    // Reset dimensions for editing
    this.dimensionHelper.resetEditingDimensions()

    // Clear component state
    this.component.hidePopover()
    this.component.closeToolHelpBox()

    // Start new tool
    this.currentTool = tool
    this.currentTool.onToolActive(options)

    this.component.updateSelectedTool(toolType)

    this.scene.add(this.currentTool.sceneObject)
    this.render()
  }

  cancelActiveTool() {
    this.clearSelection()

    // Revert back to select tool
    this.startTool(GlassDesignerTypes.ToolTypes.selectTool)
  }

  flagRenderRequired() {
    this.requiresRender = true
  }

  setMouseCursorHand() {
    if (this.mouseCursor != 'pointer') {
      this.mouseCursor = 'pointer'
      this.element.style.cursor = this.mouseCursor
    }
  }

  dimensionLabelClicked(dimension) {
    switch (dimension.dimType) {

      // If a shape dimension, start the shape edit dimension tool
      case GlassDesignerTypes.DimensionTypes.linearGlassSegmentDimension:
        if(this.isEditingAvailable()) {
          this.startTool(GlassDesignerTypes.ToolTypes.dimensionEditTool, {
            dimension: dimension,
          })
        }
        break

      case GlassDesignerTypes.DimensionTypes.angularGlassSegmentDimension:
        if(this.isEditingAvailable()) {
          this.startTool(GlassDesignerTypes.ToolTypes.dimensionEditTool, {
            dimension: dimension,
          })
        }
        break

      // Shape overall dimension editing
      case GlassDesignerTypes.DimensionTypes.linearGlassOverallDimension:
        if(this.isEditingAvailable()) {
          this.startTool(GlassDesignerTypes.ToolTypes.dimensionEditTool, {
            dimension: dimension,
          })
        }
        break

      // If an editing dimension, go through the current tool
      // Note: Allowed even if not in editing mode, as measure tool might need adjusting of editing line dimensions
      case GlassDesignerTypes.DimensionTypes.editingLineDimension:
      case GlassDesignerTypes.DimensionTypes.editingCutoutDimension:
      case GlassDesignerTypes.DimensionTypes.editingPointDimension:
        if (this.currentTool) {
          this.currentTool.editingDimensionClicked(dimension)
        }
        break

      // Hole offset dimension
      case GlassDesignerTypes.DimensionTypes.glassHoleOffsetDimension:
        if(this.isEditingAvailable()) {
          this.startTool(GlassDesignerTypes.ToolTypes.dimensionEditTool, {
            dimension: dimension,
          })
        }
        break
    }
  }

  setShapeSelectionEnabled(enabled) {
    this.shapeSelectionEnabled = enabled

    if (!enabled) {
      this.clearSelection()
    }
  }
  resetSelectionEnabled() {
    this.shapeSelectionEnabled = true
  }

  setDimensionsEnabled(dimTypeList) {
    this.dimensionTypesEnabled = dimTypeList

    // Update dimensions. Will hide with dimensions disabled
    this.setOptionDisplayDimensions(this.optionDimensionsVisible)
  }
  resetDimensionsEnabled() {
    this.setDimensionsEnabled([GlassDesignerTypes.DimensionTypes.all])
  }


  /**
   * Check if an object is selectable using selection enabled flags
   */
  isObjectSelectable(obj) {
    if((obj instanceof GlassShape) || (obj instanceof GlassDatumLine) || (obj instanceof GlassSplitLine) || (obj instanceof GlassBaseHole))
      return this.shapeSelectionEnabled

    return true
  }


  /**
   * Calculate all the projection/unprojection data for a mouse position
   * Returns an object with mouse details
   */
  calculateMouseProjection(mouseEvent) {
    this.mousePosition.x = (mouseEvent.offsetX / this.settings.viewportWidth) * 2 - 1
    this.mousePosition.y = -(mouseEvent.offsetY / this.settings.viewportHeight) * 2 + 1

    this.mouseUnproject.set(this.mousePosition.x, this.mousePosition.y, (this.camera.near + this.camera.far) / (this.camera.near - this.camera.far))
    this.mouseUnproject.unproject(this.camera)

    let radiusCheckPos = ((mouseEvent.offsetX + this.settings.mouseRadius) / this.settings.viewportWidth) * 2 - 1

    // Calculate mouse radius in world coordinates
    var radiusCalc = new THREE.Vector3()
    radiusCalc.set(radiusCheckPos, this.mousePosition.y, (this.camera.near + this.camera.far) / (this.camera.near - this.camera.far))
    radiusCalc.unproject(this.camera)
    let worldTouchRadius = radiusCalc.distanceTo(this.mouseUnproject)

    this.raycaster.setFromCamera(this.mousePosition, this.camera)

    this.calculateSnappedRaycast(this.mouseUnproject)

    return {
      offsetX: mouseEvent.offsetX,
      offsetY: mouseEvent.offsetY,
      viewportScalar: this.mousePosition,
      radius: worldTouchRadius,
      raycaster: this.raycaster,
      snapRaycaster: this.snapRaycaster,
      position: this.mouseUnproject,
      isSnapEnabled: this.getOptionSnapEnabled(),
      isSnapGridEnabled: this.getOptionSnapGridEnabled(),
      snappedPosition: this.snapUnproject
    }
  }


  /**
   * Calculate all the projection/unprojection data for a touch position
   * If 2 touches, uses the centre point
   * Returns an object with mouse/touch details
   */
  calculateTouchProjection(touchEvent) {
    let clientRect = this.component.$el.getBoundingClientRect()

    var touchPositionX = this.lastTouchPosition.x
    var touchPositionY = this.lastTouchPosition.y

    if (touchEvent.touches.length == 1) {
      touchPositionX = touchEvent.touches[0].pageX - clientRect.left
      touchPositionY = touchEvent.touches[0].pageY - clientRect.top

    } else if (touchEvent.touches.length > 1) {
      // Centre point of the touches
      var centreX = 0
      var centreY = 0

      for (var touchIndex = 0; touchIndex < touchEvent.touches.length; touchIndex++) {
        let touch = touchEvent.touches.item(touchIndex)
        centreX += touch.pageX
        centreY += touch.pageY
      }
      centreX /= touchEvent.touches.length
      centreY /= touchEvent.touches.length

      touchPositionX = centreX - clientRect.left
      touchPositionY = centreY - clientRect.top
    }

    // Only set touch position if provided
    // Reuse previous position if touch released
    if (touchEvent.touches.length > 0) {
      this.touchPosition.x = (touchPositionX / this.settings.viewportWidth) * 2 - 1
      this.touchPosition.y = -(touchPositionY / this.settings.viewportHeight) * 2 + 1

      this.lastTouchPosition.x = touchPositionX
      this.lastTouchPosition.y = touchPositionY
    }

    this.mouseUnproject.set(this.touchPosition.x, this.touchPosition.y, (this.camera.near + this.camera.far) / (this.camera.near - this.camera.far))
    this.mouseUnproject.unproject(this.camera)

    let radiusCheckPos = ((touchPositionX + this.settings.touchRadius) / this.settings.viewportWidth) * 2 - 1

    // Calculate touch radius in world coordinates
    var radiusCalc = new THREE.Vector3()
    radiusCalc.set(radiusCheckPos, this.touchPosition.y, (this.camera.near + this.camera.far) / (this.camera.near - this.camera.far))
    radiusCalc.unproject(this.camera)
    let worldTouchRadius = radiusCalc.distanceTo(this.mouseUnproject)

    this.raycaster.setFromCamera(this.touchPosition, this.camera)

    this.calculateSnappedRaycast(this.mouseUnproject)

    return {
      offsetX: touchPositionX,
      offsetY: touchPositionY,
      radius: worldTouchRadius,
      viewportScalar: this.touchPosition,
      raycaster: this.raycaster,
      snapRaycaster: this.snapRaycaster,
      position: this.mouseUnproject,
      isSnapEnabled: this.getOptionSnapEnabled(),
      isSnapGridEnabled: this.getOptionSnapGridEnabled(),
      snappedPosition: this.snapUnproject
    }
  }


  /**
   * Calculate the snapped raycaster object by snapping the mouse/touch coordinates to the nearest grid element
   * Or snap to nearest shape vertex
   * Only works for 2D
   * 
   * Returns the Raycaster object with ray snapped to nearest grid/vertex
   */
  calculateSnappedRaycast(position) {
    var gridMinorSize = this.grid.getGridMinorDivisionSize(this.camera.zoom)

    let snappedX = Math.round(position.x / gridMinorSize) * gridMinorSize
    let snappedY = Math.round(position.y / gridMinorSize) * gridMinorSize

    // TODO: Get all vertices from all shapes & snap if closer

    let snapWorldCoord = new THREE.Vector3(snappedX, snappedY, 0)
    let coordPos = snapWorldCoord.project(this.camera)

    this.snapRaycaster.setFromCamera(coordPos, this.camera)

    this.snapUnproject.set(coordPos.x, coordPos.y, (this.camera.near + this.camera.far) / (this.camera.near - this.camera.far))
    this.snapUnproject.unproject(this.camera)

    return this.snapRaycaster
  }


  /**
   * Calculate the snapped angle to the nearest 45 degrees
   * Calculated in degrees
   * Assumes the snapped option is on if this function is called
   */
  calculateSnappedAngleDegrees(angle) {
    let snapAmount = 45.0

    angle = MathsUtils.wrapAngleDegrees(angle)
    return MathsUtils.roundNumber(angle / snapAmount) * snapAmount
  }


  /**
   * Calculate the zoom step index given a scale
   */
  calculateZoomStepFromScale(scale) {
    scale = MathsUtils.clampValue(scale, Config.Zooming.minZoom, Config.Zooming.maxZoom)

    let fraction = (Math.log(scale) - Math.log(Config.Zooming.minZoom)) / (Math.log(Config.Zooming.maxZoom) - Math.log(Config.Zooming.minZoom))
    return MathsUtils.lerp(0, Config.Zooming.zoomStepCount, fraction)
  }


  /**
   * Zoom the camera to focus on all the glass shapes
   */
  moveCameraToAllGlassShapes() {
    let shapeTotalBounds = this.calculateGlassShapeBounds()
    let shapePadding = [300, 280, 50, 200]    // Accounts for different dimension types on the different sides
    let defaultEmptyAreaSize = 1500

    // Setup default bounds if no shapes added
    if(shapeTotalBounds.isEmpty())
      shapeTotalBounds.set(new THREE.Vector3(-defaultEmptyAreaSize, -defaultEmptyAreaSize, -defaultEmptyAreaSize), new THREE.Vector3(defaultEmptyAreaSize, defaultEmptyAreaSize, defaultEmptyAreaSize))

    shapeTotalBounds.expandByPoint(new THREE.Vector3(shapeTotalBounds.min.x - shapePadding[3], shapeTotalBounds.min.y - shapePadding[0], 0))
    shapeTotalBounds.expandByPoint(new THREE.Vector3(shapeTotalBounds.max.x + shapePadding[1], shapeTotalBounds.max.y + shapePadding[2], 0))

    this.moveCameraToBoundingBox(shapeTotalBounds)
  }


  /**
   * Zoom the camera to focus on a single editor object
   */
  moveCameraToEditorObject(editorObject) {
    let shapeTotalBounds = new THREE.Box3()
    let shapePadding = 300
    let defaultEmptyAreaSize = 1500

    // Convert 2D to 3D box
    let objBounds = editorObject.getBoundingBox()
    if (objBounds) {
      shapeTotalBounds.expandByPoint(objBounds.min)
      shapeTotalBounds.expandByPoint(objBounds.max)
    }

    // Setup default bounds if no shapes added
    if(shapeTotalBounds.isEmpty())
      shapeTotalBounds.set(new THREE.Vector3(-defaultEmptyAreaSize, -defaultEmptyAreaSize, -defaultEmptyAreaSize), new THREE.Vector3(defaultEmptyAreaSize, defaultEmptyAreaSize, defaultEmptyAreaSize))

    shapeTotalBounds.expandByVector(new THREE.Vector3(shapePadding, shapePadding, 0))

    this.moveCameraToBoundingBox(shapeTotalBounds)
  }


  /**
   * Move & zoom the camera to focus on a 3D bounds region
   */
  moveCameraToBoundingBox(bounds) {
    if (!bounds.isEmpty()) {
      var centerPoint = new THREE.Vector3()
      bounds.getCenter(centerPoint)

      // Orthographic camera view
      if(this.camera instanceof THREE.OrthographicCamera) {
        this.camera.position.set(centerPoint.x, centerPoint.y, 0)

        let distX = (bounds.max.x - bounds.min.x)
        let distY = (bounds.max.y - bounds.min.y)

        let viewW = this.camera.right - this.camera.left
        let viewH = this.camera.bottom - this.camera.top
                
        var dstRect = MathsUtils.aspectFitResize(distX, distY, viewW, viewH)

        if (distX > 0) {
          this.zoomScale = MathsUtils.clampValue(dstRect.width / distX, Config.Zooming.minZoom, Config.Zooming.maxZoom)
          this.zoomStep = this.calculateZoomStepFromScale(this.zoomScale)

          this.camera.zoom = this.zoomScale
          this.camera.updateProjectionMatrix()
        }
      }

      // Perspective camera view
      if(this.camera instanceof THREE.PerspectiveCamera) {
        let fitOffset = 1.2
        let shapeSize = bounds.getSize(new THREE.Vector3())
        
        let maxSize = Math.max(shapeSize.x, shapeSize.y, shapeSize.z)
        let fitHeightDistance = maxSize / (2 * Math.atan(Math.PI * this.camera.fov / 360))
        let fitWidthDistance = fitHeightDistance / this.camera.aspect
        let distance = fitOffset * Math.max(fitHeightDistance, fitWidthDistance)
        
        var direction = this.cameraTarget.clone().sub(this.camera.position)
        direction = direction.normalize().multiplyScalar(distance)
        this.camera.updateProjectionMatrix()

        this.camera.position.copy(centerPoint).sub(direction)
        this.cameraTarget.copy(centerPoint)
      }

      this.onCameraUpdated()
    }
  }




  /**
   * Calculate the bounding box of all the shape objects
   */
  calculateGlassShapeBounds() {
    var totalBounds = new THREE.Box3()

    this.objectList.forEach(object => {
      if (object instanceof GlassShape || object instanceof AssemblyGroup) {
        if (object.shapeHighlightOnly)
          return

        let objBounds = object.getBoundingBox()
        if (objBounds) {
          totalBounds.expandByPoint(objBounds.min)
          totalBounds.expandByPoint(objBounds.max)
        }
      }
    })

    return totalBounds
  }


  /**
   * Calculate the bounding box of the the selected objects
   */
  calculateSelectedObject2DBounds() {
    var totalBounds = new THREE.Box2()

    let bounds3D = this.calculateSelectedObject3DBounds()
    if(bounds3D.isEmpty())
      return totalBounds

    totalBounds.expandByPoint(new THREE.Vector2(bounds3D.min.x, bounds3D.min.y))
    totalBounds.expandByPoint(new THREE.Vector2(bounds3D.max.x, bounds3D.max.y))

    return totalBounds
  }


  /**
   * Calculate the bounding box of the the selected objects
   */
  calculateSelectedObject3DBounds() {
    let selectedList = this.getSelected()
    var totalBounds = new THREE.Box3()

    selectedList.forEach(selectedObj => {
      // Handle shapes seperately with segment/vertex selections
      if (selectedObj instanceof GlassShape) {
        if (selectedObj.shapeHighlightOnly)
          return

        selectedObj.segmentList.forEach(segment => {
          if(segment.getSegmentSelected()) {
            totalBounds.expandByPoint(new THREE.Vector3(segment.startPosition.x, segment.startPosition.y, 0))
            totalBounds.expandByPoint(new THREE.Vector3(segment.endPosition.x, segment.endPosition.y, 0))
          }
          if(segment.getSegmentStartSelected())
            totalBounds.expandByPoint(new THREE.Vector3(segment.startPosition.x, segment.startPosition.y, 0))
        })        

      } else {
        let objBounds = selectedObj.getBoundingBox()
        if (objBounds) {
          totalBounds.expandByPoint(objBounds.min)
          totalBounds.expandByPoint(objBounds.max)
        }
      }
    })

    return totalBounds
  }


  /**
   * Get a list of objects of a type
   */
  getObjectListOfType(instanceClass) {
    if (_.isArray(instanceClass)) {
      return this.objectList.filter(object => {
        return instanceClass.find(cls => {
          return object instanceof cls
        }) != null
      })
    }

    return this.objectList.filter(object => object instanceof instanceClass)
  }


  /**
   * Get an object by an ID
   */
  getObjectById(id) {
    return this.objectList.find(object => object.id == id)
  }


  /**
   * Add an object to the scene
   */
  addSceneObject(object) {
    this.objectList.push(object)

    // TODO: Update visbility based on settings (eg, dimensions)

    this.requiresRender = true
  }


  /**
   * Remove an object from the scene
   */
  removeSceneObject(object) {
    this.objectList = this.objectList.filter(obj => obj.id != object.id)

    this.requiresRender = true
  }

  /**
   * Project a world coordinate into screen coordinates
   * 
   * Returns a Vector2 object with coordinates of the world position to the screen
   */
  projectWorldCoordinate(position) {
    let coordPos = new THREE.Vector3(position.x, position.y, position.z || 0).project(this.camera)

    // Scale to screen coords from viewport
    let screenX = (((coordPos.x + 1) * 0.5) * this.settings.viewportWidth)
    let screenY = ((-(coordPos.y - 1) * 0.5) * this.settings.viewportHeight)

    return new THREE.Vector2(screenX, screenY)
  }


  /**
   * Finds the closest object to a raycaster ray with added radius
   * Used for mouse/touch events
   * Radius used for touch events to help fat fingers
   * Returns an object containing the closest BaseObject that intersects the ray & other details
   * Returns null if no objects intersect the ray
   */
  findClosestObjectToRay(objectList, ray, rayRadius) {
    var closestInfo = null
    var closestDist = 0
    var closestLayer = 0


    objectList.forEach(object => {
      let intInfo = object.checkRayIntersection(ray, rayRadius)

      if (intInfo) {
        let dist = intInfo.distance
        let layer = intInfo.layer || GlassDesignerTypes.EditorUILayers.default

        if (closestInfo === null || (dist < closestDist && layer >= closestLayer) || layer > closestLayer) {
          closestInfo = {
            object: object,
            intersection: intInfo.intersection,
            objectId: intInfo.objectId,
            distance: intInfo.distance,
            tag: intInfo.tag,
            tagIndex: intInfo.tagIndex,
            tagObjectId: intInfo.tagObjectId,
            layer: layer
          }
          closestDist = dist
          closestLayer = layer
        }
      }
    })

    return closestInfo
  }


  /**
   * Update details for a shape's segment dimensions
   * Called from the dimension editor popover
   * 
   * Throws an exception from the shape segment adjustment solver if the segment could not be changed (due to shape errors)
   */
  updateShapeSegmentDimension(shape, dimensionIndex, updateData) {
    let dimensionList = this.getObjectListOfType(LinearDimension)
    let shapeDims = dimensionList.filter(dimension => {
      return dimension.isVisible() && dimension.glassShape && dimension.glassShape.id == shape.id
    })

    var segmentDimension = shapeDims.find(dim => {
      return dim.dimType == GlassDesignerTypes.DimensionTypes.linearGlassSegmentDimension && dim.glassSegmentIndex == dimensionIndex
    })

    if (segmentDimension) {
      // Apply to the actual shape if not in highlight only mode
      if (!updateData.highlightOnly) {
        this.startUndoGroup('Adjusting shape dimension')
        this.addUndoShape(shape)

        shape.setSegmentFixedLength(dimensionIndex, updateData.length, updateData.extendDirectionIndex)

        if (updateData.updateUserFixed)
          shape.setUserFixedSegmentFixedLength(dimensionIndex, updateData.isUserFixed)

        // Move center cutouts around as required
        shape.internalItemsUpdated()

        this.closeCurrentUndoGroup()

        this.onShapeUpdated(shape)
      } else {
        // Highlight only
        // Adjust the highlighted shape

        this.resetHighlightShape(shape)

        this.highlightShape.setSegmentFixedLength(dimensionIndex, updateData.length, updateData.extendDirectionIndex)
        if (updateData.updateUserFixed)
          this.highlightShape.setUserFixedSegmentFixedLength(dimensionIndex, updateData.isUserFixed)
      }
    }

    // Force a render
    this.render()
  }


  /**
   * Update details for a shape's segment angle dimensions
   * Called from the dimension editor popover
   * 
   * Throws an exception from the shape segment adjustment solver if the segment could not be changed (due to shape errors)
   */
  updateShapeSegmentAngleDimension(shape, dimensionIndex, updateData) {
    let dimensionList = this.getObjectListOfType(AngularDimension)
    let shapeDims = dimensionList.filter(dimension => {
      return dimension.isVisible() && dimension.glassShape && dimension.glassShape.id == shape.id
    })

    var segmentDimension = shapeDims.find(dim => {
      return dim.dimType == GlassDesignerTypes.DimensionTypes.angularGlassSegmentDimension && dim.glassSegmentIndex == dimensionIndex
    })

    if (segmentDimension) {
      // Apply to the actual shape if not in highlight only mode
      if (!updateData.highlightOnly) {
        this.startUndoGroup('Adjusting shape angle dimension')
        this.addUndoShape(shape)

        shape.setSegmentFixedAngle(dimensionIndex, updateData.angle, updateData.extendDirectionIndex)

        if (updateData.updateUserFixed)
          shape.setUserFixedSegmentFixedAngle(dimensionIndex, updateData.isUserFixed)

        // Move center cutouts around as required
        shape.internalItemsUpdated()

        this.closeCurrentUndoGroup()

        this.onShapeUpdated(shape)
      } else {
        // Highlight only
        // Adjust the highlighted shape

        this.resetHighlightShape(shape)

        this.highlightShape.setSegmentFixedAngle(dimensionIndex, updateData.angle, updateData.extendDirectionIndex)
        if (updateData.updateUserFixed)
          this.highlightShape.setUserFixedSegmentFixedAngle(dimensionIndex, updateData.isUserFixed)
        
      }
    }

    // Force a render
    this.render()
  }


  /**
   * Update details for a shape's segment positioning
   * Called from the editor move popover for segments & segment start points
   * 
   * Throws an exception from the shape segment adjustment solver if the segment could not be changed (due to shape errors)
   */
  updateShapeSegment(shape, segmentIndex, updateData) {
    // Apply to the actual shape if not in highlight only mode
    if (!updateData.highlightOnly) {
      this.startUndoGroup('Updating Shape Segment')
      this.addUndoShape(shape)

      if (updateData.editingType == GlassDesignerTypes.ShapeEditingTypes.segment) {
        if (updateData.segmentMoveType == GlassDesignerTypes.MoveSegmentTypes.move) {
          let isPerpendicular = updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.down || updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.up
          let offsetAmount = updateData.moveOffset

          if (updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.down || updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.left)
            offsetAmount = -offsetAmount

          shape.moveSegment(segmentIndex, offsetAmount, isPerpendicular)
        }

        if (updateData.segmentMoveType == GlassDesignerTypes.MoveSegmentTypes.extend) {
          let offsetAmount = updateData.moveOffset

          if(updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.down)
            offsetAmount = -offsetAmount

          shape.extrudeSegment(segmentIndex, offsetAmount)
        }
        if (updateData.segmentMoveType == GlassDesignerTypes.MoveSegmentTypes.taper) {
          let offsetAmount = updateData.moveOffset
          if(updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.down)
            offsetAmount = -offsetAmount

          shape.taperSegment(segmentIndex, offsetAmount)
        }
      }
      else if (updateData.editingType == GlassDesignerTypes.ShapeEditingTypes.segmentStart) {
        shape.moveSegmentStart(segmentIndex, updateData.moveOffset, updateData.moveDirection)
      }

      if (updateData.updateUserFixed)
        shape.setUserFixedSegmentFixedLength(segmentIndex, updateData.isUserFixed)

      // Move center cutouts around as required
      shape.internalItemsUpdated()

      this.closeCurrentUndoGroup()

      this.onShapeUpdated(shape)

    } else {
      // Highlight only
      // Adjust the highlighted shape

      this.resetHighlightShape(shape)

      if (updateData.editingType == GlassDesignerTypes.ShapeEditingTypes.segment) {
        if (updateData.segmentMoveType == GlassDesignerTypes.MoveSegmentTypes.move) {
          let isPerpendicular = updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.down || updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.up
          let offsetAmount = updateData.moveOffset

          if (updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.down || updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.left)
            offsetAmount = -offsetAmount

          this.highlightShape.moveSegment(segmentIndex, offsetAmount, isPerpendicular)
        }

        if (updateData.segmentMoveType == GlassDesignerTypes.MoveSegmentTypes.extend) {
          let offsetAmount = updateData.moveOffset
          if(updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.down)
            offsetAmount = -offsetAmount

          this.highlightShape.extrudeSegment(segmentIndex, offsetAmount)
        }

        if (updateData.segmentMoveType == GlassDesignerTypes.MoveSegmentTypes.taper) {
          let offsetAmount = updateData.moveOffset
          if(updateData.moveDirection == GlassDesignerTypes.MoveEditDirectionTypes.down)
            offsetAmount = -offsetAmount

          this.highlightShape.taperSegment(segmentIndex, offsetAmount)
        }
      }
      else if (updateData.editingType == GlassDesignerTypes.ShapeEditingTypes.segmentStart) {

        this.highlightShape.moveSegmentStart(segmentIndex, updateData.moveOffset, updateData.moveDirection)
      }

      if (updateData.updateUserFixed)
        this.highlightShape.setUserFixedSegmentFixedLength(segmentIndex, updateData.isUserFixed)
    }

    // Force a render
    this.render()
  }


  /**
   * Insert a new point at a point along a segment
   * Projects to the closest point on the segment
   * Ignored if the point is within x mm's from the ends
   * 
   */
  insertSegmentPoint(shape, segmentIndex, closestPoint) {
    this.startUndoGroup('Adding segment point')
    this.addUndoShape(shape)

    shape.insertSegmentPoint(segmentIndex, closestPoint)

    this.closeCurrentUndoGroup()

    this.onShapeUpdated(shape)
  }


  /**
   * Removes a segment from the shape
   */
  removeSegment(shape, segmentIndex) {
    this.startUndoGroup('Removing segment')
    this.addUndoShape(shape)

    shape.removeSegment(segmentIndex)

    this.closeCurrentUndoGroup()

    this.onShapeUpdated(shape)
  }


  /**
   * Add a center cutout (hole) to a shape
   */
  addCenterCutoutToShape(shape, hole) {
    this.startUndoGroup('Adding center cutout')
    this.addUndoShape(shape)

    try {
      shape.addCenterCutout(hole)
    } catch(err) {
      this.cancelCurrentUndoGroup()
      throw err
    }

    this.closeCurrentUndoGroup()

    // Rebuild dimensions for the shape
    this.onShapeUpdated(shape)
  }


  /**
   * Remove a center cutout (hole) from a shape
   */
  removeCenterCutoutFromShape(shape, hole) {
    this.startUndoGroup('Removing center cutout')
    this.addUndoShape(shape)

    try {
      shape.removeCenterCutout(hole)
    } catch(err) {
      this.cancelCurrentUndoGroup()
      throw err
    }

    this.closeCurrentUndoGroup()

    // Rebuild dimensions for the shape
    this.onShapeUpdated(shape)
  }


  /**
   * Remove a glass shape from the editor
   */
  removeGlassShape(shape) {
    // If the shape is part of an asssembly, we cannot remove shapes
    // They must be modified via the assembly object
    if (shape.isPartOfAssemblyGroup()) {
      this.component.errorMessage('Error', 'Cannot remove a shape that is part of an assembly. Please remove the assembly instead.')
      return
    }

    // Clear any links that would reference the shape to be removed (eg, selections)
    this.clearSelection()

    this.startUndoGroup('Removing shape')
    this.addUndoShape(shape)

    shape.dispose()
    this.removeSceneObject(shape)

    this.closeCurrentUndoGroup()

    // Rerun dimension helper
    this.rebuildAllDimensions()

    // Rerun guideline inspection
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.render()

    this.shapeListUpdated()
  }


  /**
   * Remove an assembly from the editor
   */
  removeAssembly(assembly) {
    // Clear any links that would reference the shape to be removed (eg, selections)
    this.clearSelection()

    this.startUndoGroup('Removing assembly')
    this.addUndoShape(assembly)
    
    assembly.dispose()
    this.removeSceneObject(assembly)

    this.closeCurrentUndoGroup()

    // Rerun dimension helper
    this.rebuildAllDimensions()

    // Rerun guideline inspection
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.render()

    this.shapeListUpdated()
  }


  /**
   * Reset a glass shape back to standard shape with no child objects
   * Useful when resetting an NCR object to start again (without having to remove/recreate a shape)
   */
  resetGlassShape(shape) {
    this.startUndoGroup('Resetting shape')
    this.addUndoShape(shape)

    let bounds = shape.calculateSegmentBounds()
    if(shape instanceof CircleShape)
      shape.resizeShape((bounds.max.x - bounds.min.x) / 2)
    else
      shape.resizeShape(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y)

    // Remove all child objects
    let childList = shape.children
    childList.forEach(childObj => {
      childObj.dispose()
      shape.removeChildObject(childObj)
    })

    shape.segmentListUpdated()
    shape.updateSceneObject()

    this.closeCurrentUndoGroup()

    // Rerun dimension helper
    this.rebuildAllDimensions()

    // Rerun guideline inspection
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.render()

    this.shapeListUpdated()
  }


  /**
   * Rotate a glass shape by 90 degrees clockwise
   */
  rotateGlassShape(shape) {
    this.startUndoGroup('Rotate shape')
    this.addUndoShape(shape)

    // Rotate
    try {
      shape.rotateShape(90)
    } catch(err) {
      this.cancelCurrentUndoGroup()
      throw err
    }

    shape.segmentListUpdated()
    shape.updateSceneObject()

    this.closeCurrentUndoGroup()

    // Rerun dimension helper
    this.rebuildAllDimensions()

    // Rerun guideline inspection
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.render()

    this.shapeListUpdated()
  }


  /**
   * Mirror a glass shape on X or Y axis
   */
  mirrorGlassShape(shape, mirrorX, mirrorY) {
    this.startUndoGroup('Mirror shape')
    this.addUndoShape(shape)

    // Mirror
    try {
      shape.mirrorShape(mirrorX, mirrorY)
    } catch(err) {
      this.cancelCurrentUndoGroup()
      throw err
    }

    shape.segmentListUpdated()
    shape.updateSceneObject()

    this.closeCurrentUndoGroup()

    // Rerun dimension helper
    this.rebuildAllDimensions()

    // Rerun guideline inspection
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.render()

    this.shapeListUpdated()
  }


  /**
   * Cleanup a glass shape by removing duplicate / redundant segments
   */
  cleanupGlassShapeSegments(shape) {
    this.startUndoGroup('Cleanup shape')
    this.addUndoShape(shape)

    try {
      shape.cleanupSegments()
    } catch(err) {
      this.cancelCurrentUndoGroup()
      throw err
    }

    shape.segmentListUpdated()
    shape.updateSceneObject()

    this.closeCurrentUndoGroup()

    // Rerun dimension helper
    this.rebuildAllDimensions()

    // Rerun guideline inspection
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.render()

    this.shapeListUpdated()
  }


  /**
   * Snap all points in a selected shape to nearest grid point
   */
  snapGlassShapeSegments(shape) {
    this.startUndoGroup('Snap shape segments')
    this.addUndoShape(shape)

    try {
      shape.snapSegmentsToGrid(1.0)  // Snap threshold of 1mm
    } catch(err) {
      this.cancelCurrentUndoGroup()
      throw err
    }

    shape.segmentListUpdated()
    shape.updateSceneObject()

    this.closeCurrentUndoGroup()

    // Rerun dimension helper
    this.rebuildAllDimensions()

    // Rerun guideline inspection
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.render()

    this.shapeListUpdated()
  }


  /**
   * Correct the rotation on a glass shape
   */
  correctRotationShape(shape) {
    this.startUndoGroup('Correct rotation')
    this.addUndoShape(shape)

    try {
      shape.correctRotation()
    } catch(err) {
      this.cancelCurrentUndoGroup()
      throw err
    }

    shape.segmentListUpdated()
    shape.updateSceneObject()

    this.closeCurrentUndoGroup()

    // Rerun dimension helper
    this.rebuildAllDimensions()

    // Rerun guideline inspection
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.render()

    this.shapeListUpdated()
  }


  /**
   * Shape was updated. Force an update of the dimension helper / renderer
   */
  onShapeUpdated(shape) {
    shape.segmentListUpdated()
    shape.resetReviewApprovals()
    
    // Rebuild dimensions
    this.dimensionHelper.updateGlassShapeDimensions(shape)

    // Re-run guideline inspections
    this.guidelineHelper.runGuidelineInspectionOnShape(shape)
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.render()

    this.shapeListUpdated()
  }


  /**
   * Assembly was updated. Force various updates
   */
  onAssemblyUpdated(assemblyGroup) {
    assemblyGroup.resetReviewApprovals()
    
    // Re-run guideline inspections
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.render()
  }


  /**
   * Shape list was updated
   * Update some of the settings on the UI based on available shapes
   */
  shapeListUpdated() {
    // Note: No longer required
    // Was disabling tools if all sheets are stock only, but need splitting tool available
    
    this.component.updateToolsEnabled(true)
  }


  /**
   * Update dimensions for a shape
   */
  updateShapeDimensions(shape) {
    this.dimensionHelper.updateGlassShapeDimensions(shape)
    this.render()
  }


  /**
   * Rebuild all the dimensions
   */
  rebuildAllDimensions() {
    this.dimensionHelper.resetAllDimensions()
    this.objectList.forEach(object => {
      if (object instanceof GlassShape) {
        if (object.shapeHighlightOnly)
          return

        this.dimensionHelper.updateGlassShapeDimensions(object)
      }
    })
    
    // Ensure dimensions are visible based on settings
    this.setOptionDisplayDimensions(this.optionDimensionsVisible)
    this.flagRenderRequired()
  }


  /**
   * Remove a datum line from the editor
   */
  removeDatumLine(datumLine) {
    if(!datumLine.parentObject)
      return

    let shape = datumLine.parentObject

    // Clear any links that would reference the shape to be removed (eg, selections)
    this.clearSelection()

    this.startUndoGroup('Removing datum line')
    this.addUndoShape(datumLine.parentObject)

    // Remove datum line from its shape
    shape.removeDatumLine(datumLine)

    this.closeCurrentUndoGroup()

    // Update center cutouts in the shape (in case hole linked to datum line)
    shape.internalItemsUpdated()

    // Rerun dimension helper
    this.rebuildAllDimensions()

    this.render()
  }



  /**
   * Tool editing was updated. Force an update on the dimension helper / renderer
   */
  onToolEditingUpdated(shape, editingObjectList) {
    // Rebuild dimensions for the shape & tool editing objects
    this.dimensionHelper.updateToolGlassShapeDimensions(shape, editingObjectList)

    this.render()
  }


  /**
   * Start an undo group
   */
  startUndoGroup(name) {
    this.currentUndoGroup = {
      name: name,
      shapes: []
    }
  }


  /**
   * Add a shape to the current undo group
   */
  addUndoShape(shape, commandType = GlassDesignerTypes.UndoCommandTypes.update) {
    if (!this.currentUndoGroup) {
      console.warn('No undo group currently open')
      return
    }

    this.currentUndoGroup.shapes.push({
      commandType: commandType,
      data: shape.serialize()
    })
  }


  /**
   * Close the current undo group
   */
  closeCurrentUndoGroup() {
    if (this.currentUndoGroup) {
      // Only add group if shapes added to the group
      if(this.currentUndoGroup.shapes.length > 0) {
        this.undoGroupList.push(this.currentUndoGroup)

        // Trim the old undo groups out to save memory
        if (this.undoGroupList.length > this.settings.maxUndoHistory)
          this.undoGroupList.splice(0, 1)
      }
    }

    this.currentUndoGroup = null
  }


  /**
   * Cancel the current undo group & don't add to the undo list
   */
  cancelCurrentUndoGroup() {
    this.currentUndoGroup = null
  }


  /**
   * Undo the last undo action
   */
  undoLastAction() {
    if (this.undoGroupList.length == 0)
      return

    // Cleanup
    this.clearSelection()
    this.dimensionHelper.resetAllDimensions()

    var lastUndoGroup = this.undoGroupList.pop()

    if (lastUndoGroup.shapes) {
      lastUndoGroup.shapes.forEach(shapeInfo => {
        var existingObj = this.objectList.find(obj => obj.id == shapeInfo.data.id)

        if(shapeInfo.commandType == GlassDesignerTypes.UndoCommandTypes.add) {
          // Remove the created shape
          if(existingObj) {
            existingObj.dispose()
            this.removeSceneObject(existingObj)

            this.shapeListUpdated()
          }

        } else {
          // Find existing shape & apply that          
          if (existingObj) {
            existingObj.deserialize(shapeInfo.data)

          } else {
            var newShape = EditorFactory.createObject(shapeInfo.data.class, this)
            newShape.deserialize(shapeInfo.data)

            this.shapeListUpdated()
          }
        }
      })
    }

    this.clearSelection()
   
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())
    this.component.updatePanelReviewCount(this.getObjectListPendingReview().length)

    this.rebuildAllDimensions()

    // Force a render
    this.render()
  }


  /**
   * Capture an object that will receive all mouse/touch move events
   */
  captureEventObject(obj) {
    this.eventCapturedObject = obj
  }


  /**
   * Release the captured object from receiving all mouse/touch move events
   */
  releaseCapturedObject() {
    this.eventCapturedObject = null
  }


  /**
   * Check if an object is the currently captured event object
   */
  isCapturedEventObject(obj) {
    if (this.eventCapturedObject == null)
      return false

    return obj.id == this.eventCapturedObject.id
  }


  /**
   * Library item was dragged into the editor area
   */
  libraryItemDraggedToEditor(libraryItemInfo) {
    // Flag mouse down event as started in editor to allow drag-drop
    this.mouseCapturedDownEvent = true

    switch(libraryItemInfo.type) {
      case GlassDesignerTypes.LibraryItemTypes.centerCutOut:
        this.startTool(GlassDesignerTypes.ToolTypes.addCenterCutout)
        this.currentTool.libraryItemDraggedToEditor(libraryItemInfo)
        break

      case GlassDesignerTypes.LibraryItemTypes.edgeCutOut:
        this.startTool(GlassDesignerTypes.ToolTypes.addEdgeCutout)
        this.currentTool.libraryItemDraggedToEditor(libraryItemInfo)
        break

      case GlassDesignerTypes.LibraryItemTypes.cornerCutOut:
        this.startTool(GlassDesignerTypes.ToolTypes.addCornerCutout)
        this.currentTool.libraryItemDraggedToEditor(libraryItemInfo)
        break

      case GlassDesignerTypes.LibraryItemTypes.panelItem:
        this.startTool(GlassDesignerTypes.ToolTypes.addPanelItem)
        this.currentTool.libraryItemDraggedToEditor(libraryItemInfo)
        break

      case GlassDesignerTypes.LibraryItemTypes.shape:
      case GlassDesignerTypes.LibraryItemTypes.kit:
      case GlassDesignerTypes.LibraryItemTypes.assembly:
        this.startTool(GlassDesignerTypes.ToolTypes.addGlassShape)
        this.currentTool.libraryItemDraggedToEditor(libraryItemInfo)
        break
    }

    this.render()
  }


  /**
   * Load the textures
   * Synchronous
   */
  loadTexturesSync() {
    // Note: Textures are returned to be used immediately but loaded asynchronously by THREE.js in the background
    let textureLoader = new THREE.TextureLoader()

    this.resources.vertexTextureHandle = textureLoader.load('/designer/textures/vertex-sm.png')
    this.resources.vertexTexture = textureLoader.load('/designer/textures/vertex-sm-glass.png')
    this.resources.vertexTextureShapeSelected = textureLoader.load('/designer/textures/vertex-sm-red.png')
    this.resources.arrowTextureBG = textureLoader.load('/designer/textures/arrow-background.png')
    this.resources.arrowTextureFG = textureLoader.load('/designer/textures/arrow-outline.png')
    this.resources.arrowTextureBG = textureLoader.load('/designer/textures/arrow-move-bg.png')
    this.resources.arrowTextureFG = textureLoader.load('/designer/textures/arrow-move-fg.png')
    this.resources.arrowTextureFill = textureLoader.load('/designer/textures/arrow-move-fill.png')
    this.resources.arrowTextureRotate = textureLoader.load('/designer/textures/arrow-rotate.png')
    this.resources.arrowTextureMoveHorz = textureLoader.load('/designer/textures/arrow-move-horz.png')
    this.resources.arrowTextureMoveAll = textureLoader.load('/designer/textures/arrow-move-all.png')
    

    // Setup glass textures
    this.resources.panelGrainTextureVertical = textureLoader.load('/designer/textures/reeded-vertical.png')
    this.resources.panelGrainTextureVertical.wrapS = THREE.RepeatWrapping
    this.resources.panelGrainTextureVertical.wrapT = THREE.RepeatWrapping
    this.resources.panelGrainTextureVertical.flipY = false
    this.resources.panelGrainTextureVertical.rotation = 0

    this.resources.panelGrainTextureHorizontal = textureLoader.load('/designer/textures/reeded-vertical.png')
    this.resources.panelGrainTextureHorizontal.wrapS = THREE.RepeatWrapping
    this.resources.panelGrainTextureHorizontal.wrapT = THREE.RepeatWrapping
    this.resources.panelGrainTextureHorizontal.flipY = false
    this.resources.panelGrainTextureHorizontal.rotation = Math.PI / 2

    this.resources.panelCathedralTexture = textureLoader.load('/designer/textures/glass-decorated.png')
    this.resources.panelCathedralTexture.wrapS = THREE.RepeatWrapping
    this.resources.panelCathedralTexture.wrapT = THREE.RepeatWrapping
    this.resources.panelCathedralTexture.flipY = false

    this.resources.panelSatinliteTexture = textureLoader.load('/designer/textures/glass-satinlite.png')
    this.resources.panelSatinliteTexture.wrapS = THREE.RepeatWrapping
    this.resources.panelSatinliteTexture.wrapT = THREE.RepeatWrapping
    this.resources.panelSatinliteTexture.flipY = false

    this.resources.panelSpotswoodTexture = textureLoader.load('/designer/textures/glass-spotswood.png')
    this.resources.panelSpotswoodTexture.wrapS = THREE.RepeatWrapping
    this.resources.panelSpotswoodTexture.wrapT = THREE.RepeatWrapping
    this.resources.panelSpotswoodTexture.flipY = false
    
    this.resources.iconTextureBG = textureLoader.load('/designer/textures/icon-ring.png')
    this.resources.iconTextureFill = textureLoader.load('/designer/textures/arrow-move-fill.png')
    this.resources.iconWarningTexture = textureLoader.load('/designer/textures/icon-warning.png')
    this.resources.iconApprovedTexture = textureLoader.load('/designer/textures/icon-approved.png')
  }


  /**
   * Load a texture for a library item (eg, panel item)
   * Returns the Texture object immediately. Background process will download the library item & load the texture
   * Synchronous
   */
  loadEditorLibraryItemTextureSync(orderEditorLibraryItemId) {
    if(orderEditorLibraryItemId == null)
      return null

    // Check if already loaded
    if(this.resources.libraryItemTextures[orderEditorLibraryItemId])
      return this.resources.libraryItemTextures[orderEditorLibraryItemId]

    var texture = new THREE.Texture()
    this.resources.libraryItemTextures[orderEditorLibraryItemId] = texture

    // Load editor item
    let glassEditor = this

    this.component.$apiService.glassDesignerService.getLibraryItem(orderEditorLibraryItemId)
    .then(response => {
      let textureData = response.data.displayTextureData
      if(textureData) {
        let imageLoader = new THREE.ImageLoader()

        // Load from the data URI
        imageLoader.load(textureData, function ( image ) {
          texture.image = image
    
          // JPEGs can't have an alpha channel, so memory can be saved by storing them as RGB.
          /* eslint-disable no-useless-escape */
          const isJPEG = textureData.search( /\.jpe?g($|\?)/i ) > 0 || textureData.search( /^data\:image\/jpeg/ ) === 0
    
          texture.format = isJPEG ? THREE.RGBFormat : THREE.RGBAFormat
          texture.needsUpdate = true

          glassEditor.flagRenderRequired()
        })        
      }
    })
    .catch(err => {
      console.error(err)
    })

    return texture
  }


  /**
   * Load a texture for the order stamp image data
   * Returns the Texture object
   * Synchronous
   */
  loadOrderStampTextureSync() {
    // Check if already loaded
    if(this.resources.orderStampImageTexture)
      return this.resources.orderStampImageTexture

    var orderStampConfig = this.getEditorConfigurationData().orderStampConfig
    if(!orderStampConfig || !orderStampConfig.stampImageData)
      return null

    var texture = new THREE.Texture()
    this.resources.orderStampImageTexture = texture

    // Load editor item
    var glassEditor = this

    var imageLoader = new THREE.ImageLoader()
    var textureData = (orderStampConfig.stampImageData + '')

    // Load from the data URI
    try {
      imageLoader.load(textureData, function ( image ) {
        texture.image = image

        // JPEGs can't have an alpha channel, so memory can be saved by storing them as RGB.
        /* eslint-disable no-useless-escape */
        const isJPEG = textureData.search( /\.jpe?g($|\?)/i ) > 0 || textureData.search( /^data\:image\/jpeg/ ) === 0

        texture.format = isJPEG ? THREE.RGBFormat : THREE.RGBAFormat
        texture.needsUpdate = true

        glassEditor.flagRenderRequired()
      })
    } catch(err) {
      console.error(err)
    }

    return texture
  }


  /**
   * Create a linear gradient texture for 2 colours
   * Caches textures for common gradients
   */
  createLinearGradientTexture(colour1, colour2, angleRadians = 0) {
    var gradKey = JSON.stringify({c1: colour1, c2: colour2, a: Math.round(angleRadians * 180/Math.PI)})
    if(this.textureCache[gradKey])
      return this.textureCache[gradKey]

    var gradTexture = EditorMaterialUtils.createLinearGradientTexture(colour1, colour2, angleRadians)
    this.textureCache[gradKey] = gradTexture

    return gradTexture
  }


  /**
   * Calculates the next available location to place a shape
   * Used for auto-adding shapes (eg, importing)
   * Ensures shapes have spacing with no overlaps & don't go outside the world bounds
   */
  calculateFreeShapePosition(width, height) {
    let minSpacing = 500
    let maxWorldRight = this.settings.maxWorldWidth / 2
    let maxWorldBottom = this.settings.maxWorldHeight / 2
    
    // Convert all shapes to rectangles
    var usedRects = this.getShapeList().map(glassShape => glassShape.calculateSegmentBounds())

    var testBox = new THREE.Box2()

    // Test each location as a grid
    // Only currently uses X+, Y+ region. Could in future extend to use X-/Y- areas of the scene
    // This way attempts to keep consistent with the import positions
    for(var y=0; y<maxWorldBottom - height - minSpacing; y += minSpacing) {
      for(var x=0; x<maxWorldRight - width - minSpacing; x += minSpacing) {

        testBox.min.x = x - minSpacing
        testBox.min.y = y - minSpacing
        testBox.max.x = x + width + minSpacing
        testBox.max.y = y + height + minSpacing

        // Check for intersections
        let int = usedRects.find(rect => {
          return rect.intersectsBox(testBox)
        })

        if(!int)
          return new THREE.Box2(new THREE.Vector2(x, y), new THREE.Vector2(x + width, y + height))
      }
    }

    return null
  }


  /**
   * Display a highlighted region from the order editor review panel
   * Region is a bounds object
   */
  displayReviewHighlightRegion(regionBounds) {
    this.reviewHighlightObj.updateDisplayRegion(regionBounds)
    this.reviewHighlightObj.setVisible(true)

    this.render()
  }


  /**
   * Hide the highlighted region from the order editor review panel
   */
  hideReviewHighlightRegion() {
    this.reviewHighlightObj.setVisible(false)
    
    this.render()
  }


  /**
   * Import shape data objects as GlassShape objects
   */
  importGlassShapeList(importData) {
    this.startUndoGroup('Importing panels')

    let shapeDataList = importData.shapeList

    // Calculate the bounds for the entire shape & move to centre
    var totalBounds = new THREE.Box2()
    shapeDataList.forEach(shapeData => {
      totalBounds.union(shapeData.bounds)
    })

    shapeDataList.forEach(shapeData => {
      let shapeWidth = Math.round(shapeData.bounds.max.x - shapeData.bounds.min.x)
      let shapeHeight = Math.round(shapeData.bounds.max.y - shapeData.bounds.min.y)

      let freePosition = this.calculateFreeShapePosition(shapeWidth, shapeHeight)
      if(!freePosition)
        return

      var left = -shapeData.bounds.min.x + freePosition.min.x
      var top = -shapeData.bounds.min.y + freePosition.min.y

      // Start with rectangle shape & override the segment list
      let newShape = new RectangleShape(this, null, {
        width: shapeWidth,
        height: shapeHeight,
        quantity: 1,
        stockId: importData.stockId,
        stockPriceGroupId: importData.stockPriceGroupId,
        glassLevelId: 0,
        isProcessedItem: true,
        description: importData.description,
        stock: _.cloneDeep(importData.stock),
        stockPriceGroup: _.cloneDeep(importData.stockPriceGroup),
        unitPrice: importData.unitPrice,
        defaultEdgeworkConfigId: importData.defaultEdgeworkConfigId,
        isStamped: importData.isStamped
      })
      

      newShape.segmentList = shapeData.segmentList.map(segmentInfo => {
        let segment = segmentInfo.clone()

        // Adjust positions
        segment.startPosition.x += left
        segment.startPosition.y += top
        segment.endPosition.x += left
        segment.endPosition.y += top
        if(segment.arcCenterPosition) {
          segment.arcCenterPosition.x += left
          segment.arcCenterPosition.y += top
        }
        segment.arcUpdated()

        return segment
      })

      newShape.segmentListUpdated()

      // Center cutouts
      shapeData.holeList.forEach(holeData => {
        var holeObject = null

        // Rectangle / Circle
        if(holeData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
          holeObject = new RectangleHole(this, {
            width: parseInt(holeData.width || 100),
            height: parseInt(holeData.height || 50),
            cornerRadiusTL: parseFloat(holeData.cornerRadiusTL || 5),
            cornerRadiusTR: parseFloat(holeData.cornerRadiusTR || 5),
            cornerRadiusBR: parseFloat(holeData.cornerRadiusBR || 5),
            cornerRadiusBL: parseFloat(holeData.cornerRadiusBL || 5),
            angle: parseInt(holeData.angle || 0),
            isCutoutPolished: !!holeData.isCutoutPolished
          })
        } else if(holeData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
          holeObject = new CircleHole(this, {
            radius: parseFloat(holeData.radius || 5),
            isCutoutPolished: !!holeData.isCutoutPolished,
            isCountersunk: !!holeData.isCountersunk,
            countersunkRadius: parseFloat(holeData.countersunkRadius || 0)
          })
        } else if(holeData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.oval) {
          holeObject = new OvalHole(this, {
            width: parseFloat(holeData.width || 5),
            height: parseFloat(holeData.height || 5),
            isCutoutPolished: !!holeData.isCutoutPolished
          })
        } else if(holeData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
          
          // Custom holes
          holeObject = new CustomHole(this, {
            isOpenShape: false,
            isCornerShape: false,
            segmentList: holeData.segmentList
          })

        } else {
          // TODO: Custom cutout type
          // Unknown cutout type
          return
        }

        var holeDatumLinks = [
          new DatumLink(),
          new DatumLink()
        ]

        var holePosition = holeData.position.clone().add(new THREE.Vector2(left, top))
        holeObject.setAnchorInfo(GlassDesignerTypes.HoleMeasurePointTypes.center, GlassDesignerTypes.HoleMeasurePointTypes.middle)
        let validDatumSolution = EditorDatumUtils.calculateClosestDatumLinks(newShape, holeDatumLinks, holePosition, { ignoreAllHoles: true })

        holeObject.setDatumLinks(holeDatumLinks)
        
        // If the hold datum links are not valid, place the hole just from the edge
        // The position imported in the DXF will be correct
        if(holeObject.hasInvalidDatumLink() || !validDatumSolution) {
          EditorDatumUtils.calculateClosestDatumLinks(newShape, holeDatumLinks, holePosition, { forceShapeBounds: true })
          holeObject.setDatumLinks(holeDatumLinks)
        }

        newShape.addCenterCutout(holeObject)
      })

      newShape.segmentListUpdated()
      newShape.updateSceneObject()
      newShape.applyDefaultEdgework()
      newShape.applyDefaultPanelItems()
      newShape.updateDefaultStampItem()
      this.onShapeUpdated(newShape)

      this.addUndoShape(newShape, GlassDesignerTypes.UndoCommandTypes.add)
    })

    this.closeCurrentUndoGroup()

    // Recalculate dimensions
    this.objectList.forEach(object => {
      if (object instanceof GlassShape) {
        if (object.shapeHighlightOnly)
          return

        this.dimensionHelper.updateGlassShapeDimensions(object)
      }
    })

    // Move view to shapes
    this.moveCameraToAllGlassShapes()

    this.render()
  }


  /**
   * Convert a list of regular, non-editor order items in the order into proper rectangle shapes
   * 
   */
  convertLineOrderItems(convertData) {
    this.startUndoGroup('Converting items')

    // Convert all order items
    convertData.forEach(orderItemInfo => {
      let orderItem = this.getOrder().getOrderItemById(orderItemInfo.id)
      if(!orderItem)
        return

      // Find all services
      // We can convert edgework
      let services = this.getOrder().orderItems.filter(childItem => childItem.parentOrderItemId == orderItem.id)

      let shapeWidth = orderItem.glassWidth
      let shapeHeight = orderItem.glassHeight

      let freePosition = this.calculateFreeShapePosition(shapeWidth, shapeHeight)
      if(!freePosition)
        return

      // Start with rectangle shape & override the segment list
      let newShape = new RectangleShape(this, null, {
        width: shapeWidth,
        height: shapeHeight,
        quantity: orderItem.quantity,
        stockId: orderItem.stockId,
        stockPriceGroupId: orderItem.stockPriceGroupId,
        glassLevelId: 0,
        isProcessedItem: true,
        description: orderItem.description,
        stock: _.cloneDeep(orderItem.stock),
        stockPriceGroup: _.cloneDeep(orderItem.stockPriceGroup),
        unitPrice: orderItem.unitPrice,
        defaultEdgeworkConfigId: orderItem.defaultEdgeworkConfigId,
        isStamped: orderItem.isStamped
      })

      newShape.updateOrderItemData(orderItem, this.getOrder().orderItems)
      newShape.moveShape(freePosition.min)
      newShape.segmentListUpdated()

      let glassPanelShapeTypeId = newShape.calculateShapePriceTypeId()

      // Edgework services
      services.forEach(serviceOrderItem => {
        let serviceProcessed = false

        // Find if an edgework config matches up
        let edgeworkInfo = this.configurationData.edgeworkList.find(edgeworkData => {
          if(edgeworkData.orderEditorEdgeworkStocks) {
            return edgeworkData.orderEditorEdgeworkStocks.some(edgeworkStock => {
              if(edgeworkStock.stockId != serviceOrderItem.stockId)
                return false
              if(edgeworkStock.orderEditorShapePriceTypeId) {
                if(edgeworkStock.orderEditorShapePriceTypeId != glassPanelShapeTypeId)
                  return false
              }
              return true
            })
          }
          return false
        })
        if(edgeworkInfo) {

          // Apply based on edge flag rules
          if(_.isArray(serviceOrderItem.edgeworkFlagList)) {

            // Set the segment edgework based on the flags
            newShape.segmentList.forEach(segment => {
              let segEdgeworkFlag = newShape._calculateEdgeworkFlag(segment.startPosition, segment.endPosition)

              if(serviceOrderItem.edgeworkFlagList.includes(segEdgeworkFlag)) {
                newShape.updateSegmentEdgework(segment, edgeworkInfo.id, 0)

                serviceProcessed = true
              }
            })
          }
        }

        // Flag the service as being used by the editor
        if(serviceProcessed) {
          serviceOrderItem.isEditorGeneratedItem = true
          serviceOrderItem.editorData = {
            editorType: GlassDesignerTypes.EditorOrderItemDataTypes.edgeworkService,
            stockId: serviceOrderItem.stockId,
            glassShapeId: newShape.id
          }
        }
      })

      // Stamp stock settings
      services.forEach(serviceOrderItem => {
        let serviceProcessed = false

        if(this.configurationData.orderStampConfig) {
          // Stamp required?
          if(serviceOrderItem.stockId == this.configurationData.orderStampConfig.stampRequiredStockId) {
            newShape.isStamped = true
            serviceProcessed = true
          }

          // Stamp not required?
          if(serviceOrderItem.stockId == this.configurationData.orderStampConfig.stampNotRequiredStockId) {
            newShape.isStamped = false
            serviceProcessed = true
          }
        }

        // Flag the service as being used by the editor
        if(serviceProcessed) {
          serviceOrderItem.isEditorGeneratedItem = true
          serviceOrderItem.editorData = {
            editorType: GlassDesignerTypes.EditorOrderItemDataTypes.stampService,
            stockId: serviceOrderItem.stockId,
            glassShapeId: newShape.id
          }
        }
      })
      
      newShape.updateSceneObject()
      newShape.applyDefaultEdgework()
      newShape.applyDefaultPanelItems()
      newShape.updateDefaultStampItem()
      this.onShapeUpdated(newShape)

      // Link the order item to the new shape
      orderItem.isEditorGeneratedItem = true
      orderItem.editorData = {
        editorType: GlassDesignerTypes.EditorOrderItemDataTypes.glassShape,
        glassShapeId: newShape.id,
        data: {}
      }

      this.addUndoShape(newShape, GlassDesignerTypes.UndoCommandTypes.add)
    })


    this.closeCurrentUndoGroup()

    // Recalculate dimensions
    this.objectList.forEach(object => {
      if (object instanceof GlassShape) {
        if (object.shapeHighlightOnly)
          return

        this.dimensionHelper.updateGlassShapeDimensions(object)
      }
    })

    // Move view to shapes
    this.moveCameraToAllGlassShapes()

    this.render()
  }


  /**
   * A guideline warning in the list was clicked on
   * Move the viewport to focus on the warning position
   */
  guidelineWarningClicked(guidelineWarningId) {
    let guidelineWarning = this.guidelineHelper.getWarningById(guidelineWarningId)
    if(!guidelineWarning)
      return

    this.camera.position.x = guidelineWarning.position.x
    this.camera.position.y = guidelineWarning.position.y

    let warningZoom = 2.0     // Fixed

    this.zoomScale = MathsUtils.clampValue(warningZoom, Config.Zooming.minZoom, Config.Zooming.maxZoom)
    this.zoomStep = this.calculateZoomStepFromScale(this.zoomScale)

    this.camera.zoom = this.zoomScale
    this.camera.updateProjectionMatrix()
    
    this.onCameraUpdated()
  }


  onReceiveObjectEvent(object, eventType, data) {
    // Send object events to the active tool
    if(this.currentTool) {
      this.currentTool.onReceiveObjectEvent(object, eventType, data)
    }
  }


  getMoveHandleObject() {
    return this.moveHandle
  }


  /**
   * Cleanup the panels in the editor by auto-arranging the panels into a grid
   */
  arrangePanels() {
    let minSpacing = 1000
    let maxWorldRight = this.settings.maxWorldWidth / 2 - minSpacing*2
    let maxWorldBottom = this.settings.maxWorldHeight / 2 - minSpacing*2

    let shapeList = this.getShapeList()
    let assemblyList = this.getAssemblyGroupList()

    // Remove assembly children from list
    shapeList = shapeList.filter(glassShape => !glassShape.isPartOfAssemblyGroup())

    this.startUndoGroup('Auto arranging panels')

    let curPosX = 0
    let curPosY = 0
    let curRowHeight = 0
    let totalBounds = new THREE.Box2()

    let moveNextRow = () => {
      curPosX = 0
      curPosY += curRowHeight
      curRowHeight = 0
    }

    // Move shapes
    shapeList.forEach(glassShape => {
      this.addUndoShape(glassShape)

      let bounds = glassShape.calculateSegmentBounds()

      if(curPosX + (bounds.max.x - bounds.min.x) + minSpacing >= maxWorldRight)
        moveNextRow()

      glassShape.moveShape(new THREE.Vector2(curPosX - bounds.min.x, curPosY - bounds.min.y))

      curRowHeight = Math.max(curRowHeight, (bounds.max.y - bounds.min.y) + minSpacing)
      curPosX += (bounds.max.x - bounds.min.x) + minSpacing

      bounds = glassShape.calculateSegmentBounds()
      totalBounds.union(bounds)

      if(curPosX >= maxWorldRight)
        moveNextRow()
    })

    // Move assemblies
    assemblyList.forEach(assemblyGroup => {
      this.addUndoShape(assemblyGroup)

      let bounds = assemblyGroup.bounds

      if(curPosX + (bounds.max.x - bounds.min.x) + minSpacing >= maxWorldRight)
        moveNextRow()

      assemblyGroup.moveAssemblyGroup(new THREE.Vector2(curPosX - bounds.min.x, curPosY - bounds.min.y))

      curRowHeight = Math.max(curRowHeight, (bounds.max.y - bounds.min.y) + minSpacing)
      curPosX += (bounds.max.x - bounds.min.x) + minSpacing

      bounds = assemblyGroup.bounds
      totalBounds.union(bounds)

      if(curPosX >= maxWorldRight)
        moveNextRow()
    })

    // Calculate shape total bounds
    // Center all the shapes around origin
    let adjustPos = new THREE.Vector2(-(totalBounds.max.x - totalBounds.min.x)/2, -(totalBounds.max.y - totalBounds.min.y)/2)
    shapeList.forEach(glassShape => {
      glassShape.moveShape(adjustPos)
    })
    assemblyList.forEach(assemblyGroup => {
      assemblyGroup.moveAssemblyGroup(adjustPos)
    })

    this.closeCurrentUndoGroup()

    // Rerun dimension helper
    this.rebuildAllDimensions()

    // Rerun guideline inspection
    this.guidelineHelper.runFullGuidelineInspection()
    this.component.updateWarningList(this.guidelineHelper.getWarningList())

    this.render()

    this.shapeListUpdated()
  }


  /**
   * Find an available position for a new shape
   * Used for cloning / seperating shapes
   * 
   * Returns a Vector2 for the top-left point of the available position
   */
  getAvailableShapePosition(requiredBounds, shapePadding = 400) {
    var availablePosition = new THREE.Vector2(0, 0)

    // Find rightmost available spot
    // FUTURE: Split into a grid & move through cells
    this.getShapeList().forEach((shape, shapeIndex) => {
      let bounds = shape.calculateSegmentBounds()
      if(!bounds.isEmpty()) {
        availablePosition.x = Math.max(availablePosition.x, bounds.max.x + shapePadding)

        if(shapeIndex == 0)
          availablePosition.y = bounds.min.y

        availablePosition.y = Math.min(availablePosition.y, bounds.min.y)
      }
    })

    return availablePosition
  }


  /**
   * Generate the DXF for all the shapes in the scene
   * Exports shapes as-is
   * 
   * Returns a string containing full DXF data
   */
  generateExportDXF() {
    let dxfWriter = new DXFWriter()
    let scaleX = 1.0
    let scaleY = -1.0   // DXF Y coordinate system is flipped to ours

    var flipPathWinding = false
    if(scaleY < 0)
      flipPathWinding = !flipPathWinding
    if(scaleX < 0)
      flipPathWinding = !flipPathWinding

    dxfWriter.startSection('ENTITIES')
    dxfWriter.setCurrentLayer('Outline')

    let exportMatrix = new THREE.Matrix3()
    exportMatrix.scale(scaleX, scaleY)     

    // Export each shape
    let shapeList = this.getShapeList()
    shapeList.forEach(shape => {
      let centerCutouts = shape.getCenterCutoutList()

      // Outline
      let transformSegments = this.transformSegmentListForDXFExport(shape.segmentList, exportMatrix, scaleX, scaleY)
      this.writeSegmentListDXFWriter(dxfWriter, transformSegments)

      // Center cutouts
      centerCutouts.forEach(centerCutout => {
        if(centerCutout.holeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
          // Circle
          let bounds = centerCutout.calculateSegmentBounds()
          let boundsCenter = bounds.getCenter(new THREE.Vector2())

          let centerPnt = new THREE.Vector2(boundsCenter.x, boundsCenter.y)
          centerPnt.applyMatrix3(exportMatrix)

          dxfWriter.writeCircleEntity(centerPnt.x, centerPnt.y, centerCutout.radius)

        } else {
          let transformSegments = this.transformSegmentListForDXFExport(centerCutout.segmentList, exportMatrix, scaleX, scaleY)

          let isClockwise = MathsUtils.getGlassSegmentListOrientation(transformSegments) < 0
          if(flipPathWinding)
            isClockwise = !isClockwise
          if(isClockwise)
            MathsUtils.reverseGlassSegmentList(transformSegments)

          this.writeSegmentListDXFWriter(dxfWriter, transformSegments)
        }
      })
    })

    return dxfWriter.export()
  }


  /**
   * Transform a segment list by a matrix & coord system
   */
  transformSegmentListForDXFExport(segmentList, exportMatrix, coordSystemScaleX, coordSystemScaleY) {
    return segmentList.map(segment => {
      var outputSegment = new GlassSegment(segment.type, {
        startCornerRadius: segment.startCornerRadius,
        endCornerRadius: segment.endCornerRadius,
      })

      outputSegment.startPosition.set(segment.startPosition.x, segment.startPosition.y)
      outputSegment.endPosition.set(segment.endPosition.x, segment.endPosition.y)

      outputSegment.startPosition.applyMatrix3(exportMatrix)
      outputSegment.endPosition.applyMatrix3(exportMatrix)

      if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
        outputSegment.arcCenterPosition = new THREE.Vector2(segment.arcCenterPosition.x, segment.arcCenterPosition.y)
        outputSegment.arcRadius = segment.arcRadius
        outputSegment.arcClockwise = segment.arcClockwise
        outputSegment.arcStartAngle = segment.arcStartAngle
        outputSegment.arcEndAngle = segment.arcEndAngle

        outputSegment.arcCenterPosition.applyMatrix3(exportMatrix)

        outputSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(outputSegment.arcCenterPosition, outputSegment.startPosition)
        outputSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(outputSegment.arcCenterPosition, outputSegment.endPosition)

        if(coordSystemScaleX < 0)
          outputSegment.arcClockwise = !outputSegment.arcClockwise
        if(coordSystemScaleY < 0)
          outputSegment.arcClockwise = !outputSegment.arcClockwise

        outputSegment.arcUpdated()
      }

      return outputSegment
    })
  }


  /**
   * Write a segment list to a DXF Writer
   */
  writeSegmentListDXFWriter(dxfWriter, segmentList, windingReversed = false) {
    segmentList.forEach((segment, segmentIndex) => {
      let prevSegment = segmentList[segmentIndex == 0 ? segmentList.length-1 : segmentIndex-1]
      let nextSegment = segmentList[segmentIndex == segmentList.length-1 ? 0 : segmentIndex+1]

      if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
        var startPos = new THREE.Vector2(segment.startPosition.x, segment.startPosition.y)
        var endPos = new THREE.Vector2(segment.endPosition.x, segment.endPosition.y)

        // Start corner radius
        if(segment.startCornerRadius > 0) {
          let roundCornerData = MathsUtils.calculateCornerRounding(
            new THREE.Vector2(prevSegment.startPosition.x, prevSegment.startPosition.y),
            new THREE.Vector2(prevSegment.endPosition.x, prevSegment.endPosition.y),
            new THREE.Vector2(segment.startPosition.x, segment.startPosition.y),
            new THREE.Vector2(segment.endPosition.x, segment.endPosition.y),
            segment.startCornerRadius,
            windingReversed
          )

          if(roundCornerData) {
            let startAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(roundCornerData.startAngle))
            let endAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(roundCornerData.endAngle))

            let outputStartAngle = roundCornerData.clockwise ? endAngle : startAngle
            let outputEndAngle = roundCornerData.clockwise ? startAngle : endAngle

            // Add arc
            dxfWriter.writeArcEntity(
              roundCornerData.center.x,
              roundCornerData.center.y,
              roundCornerData.radius,
              outputStartAngle,
              outputEndAngle
            )

            startPos = roundCornerData.segment2Start.clone()
          }
        }

        // If end corner radius is used, just pull back the end pos
        // The next segment will handle the arc for the corner
        if(segment.endCornerRadius > 0) {
          let roundCornerData = MathsUtils.calculateCornerRounding(
            new THREE.Vector2(segment.startPosition.x, segment.startPosition.y),
            new THREE.Vector2(segment.endPosition.x, segment.endPosition.y),
            new THREE.Vector2(nextSegment.startPosition.x, nextSegment.startPosition.y),
            new THREE.Vector2(nextSegment.endPosition.x, nextSegment.endPosition.y),
            segment.endCornerRadius,
            windingReversed
          )
          if(roundCornerData) {
            endPos = roundCornerData.segment1End.clone()
          }
        }

        dxfWriter.writeLineEntity(startPos.x, startPos.y, endPos.x, endPos.y)
      }

      else if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
        // TODO: Handle if consecutive arcs form a full circle, use circle instead

        let startAngle = MathsUtils.wrapAngleDegrees(segment.arcStartAngle)
        let endAngle = MathsUtils.wrapAngleDegrees(segment.arcEndAngle)

        var outputStartAngle = !segment.arcClockwise ? endAngle : startAngle
        var outputEndAngle = !segment.arcClockwise ? startAngle : endAngle

        dxfWriter.writeArcEntity(
          segment.arcCenterPosition.x,
          segment.arcCenterPosition.y,
          segment.arcRadius,
          outputStartAngle,
          outputEndAngle
        )
      }
    })
  }
}